import { LitElement, html, css, nothing} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './cos-skeleton.js';
// The "pick a library first" prompt below renders <cos-empty-state>. It was
// never imported directly here — only registered as a side effect of the
// lazily-imported cos-command-palette.js (which imports it for its own
// empty-results state). In normal use that's harmless (the palette module
// graph resolves before any UI interaction), but a test file that exercises
// the "pick a library first" prompt before ever opening the command palette
// hits it first: happy-dom parses the prompt's cached lit-html <template>
// with "cos-empty-state" still unregistered, permanently baking in an
// "undefined custom element" placeholder (happy-dom's customElements.upgrade
// is a documented no-op, so a later customElements.define() never retrofits
// it) — and every future clone of that template then applies the real
// CosEmptyState.attributeChangedCallback to a plain HTMLElement instance,
// throwing on its missing Lit internals. Only observable under
// `--sequence.shuffle` (it depends on which test runs first). Import it
// directly so its registration never depends on load order.
import '../design-system/cos-empty-state.js';
// The one breadcrumb trail (docs/design-system/breadcrumb-trail.md). Eager,
// like the bell: it is on every routed page, so a lazy chunk would only buy
// a header that pops in a frame late.
import '../design-system/cos-trail.js';
import {
	resolveTrail,
	trailIsAwaitingLeaf,
	type Crumb,
	type TrailSection,
} from '../navigation/trail-registry.js';
import { railHrefMatches, railItemMatches } from '../navigation/rail-active.js';
import { clearTrail, crumbLabelsFor, subscribeLeaf, trailLeafFor } from '../services/trail.js';
// Stage 38a — notification bell sits in the header. Eager
// import: every authenticated page renders it, so lazy-loading
// would defeat the always-on bell.
import './cos-notification-bell.js';
import { NotificationsWS } from '../services/notifications-ws.js';
import { api, apiWebSocketURL } from '../services/api.js';
import { crosswalkClientToPartnerId } from '../library/moduleTenant.js';
// Stage 34c — cos-toast static-import dropped (was the headline cause
// of the Vite "dynamic + static import" warning). The <cos-toast>
// element is still rendered in this component's template (line ~595);
// it stays an inert custom-element-shell until the first showToast()
// call triggers the lazy chunk + Custom Elements registration, at
// which point the existing element upgrades and starts handling
// cos:toast window events. The window between mount + first-toast is
// harmless — no toasts to render anyway.
import './cos-icon.js';
// Responsive Workstream C — the SW-update toast's Reload/Dismiss
// actions (check-elements.sh ratchets the raw-button-element count for
// this file at its current baseline — cos-button, not a hand-rolled
// element).
import './cos-button.js';
// Grouped admin settings sidebar — eager import is cheap (small) and
// every /admin/* route renders it, so a lazy chunk would only add a
// flash on first admin navigation.
import './cos-admin-nav.js';
// Staff feedback / issue-capture platform — the header launcher. Eager
// (tiny: badge + toggle only); the heavy panel body lazy-loads on first
// click via the same dynamic-import pattern as the admin agent panel below.
import './cos-feedback-widget.js';
import { installFeedbackContextCapture } from '../services/feedback-context.js';
import { shortcutRegistry } from '../keyboard/registry.js';
import { registerGlobalShortcuts } from '../keyboard/globals.js';
import { eventTargetsField } from '../keyboard/inputGuard.js';
import { circulateProducts } from '../services/circulate-products.js';
import { communityEnabled } from '../services/community-context.js';
// Responsive Workstream C — connectivity banner + SW-update toast.
import { connectionMonitor } from '../services/connection.js';
// Responsive Workstream A2 (roam-responsive-pwa-spec.md §6) — the single
// ViewportController shell-level code reads instead of calling matchMedia ad
// hoc. NOTE: at a2-worktree-creation time this imports a LOCAL STUB (see the
// file's own header) — sibling branch fleet/resp-a1 ships the real one at
// this same path; the stub is dropped once a1 merges.
import { ViewportController } from '../design-system/viewport-controller.js';
// Responsive Workstream B (roam-responsive-pwa-spec.md §7) — "nav on
// compact shows gated items with a desktop marker rather than hiding
// them: discoverability over mystery." One shared resolver, consulted
// per row at the CURRENT tier.
import { capabilityFor } from '../responsive/capability-registry.js';
// Partner-workspace nav customization (per-user reorder + optional groups).
import {
	applyNavLayout,
	docFromSections,
	defaultNavSections,
	moveNavItemBefore,
	moveNavItemAfter,
	moveNavItemToSectionEnd,
	addNavGroup,
	renameNavGroup,
	removeNavGroup,
	hiddenNavKeys,
	hideNavKey,
	showNavKey,
	type NavSection,
	type NavLayoutDoc,
} from '../utils/navLayout.js';
// Gates + labels shared with the /partner-admin page: which entitled
// products carry an admin surface (drives whether the "Admin" prime
// link shows at all) and each product's plain-language nav labels.
import {
	PARTNER_ADMIN_GROUPS,
	PARTNER_MODULE_LABELS,
	UNGATED_ADMIN_PRODUCTS,
	productEntitled,
	type PartnerAdminGroupInfo,
	type PartnerProduct,
} from '../partner-admin/catalog.js';
import { sidePanelManager, type PanelState } from '../panel/manager.js';
// Shared legacy-client context (2026-07-15, Paul): control-panel-ported
// modules (Broadcast today) are client-scoped for level-1 staff; the ONE
// picker lives here at the top of the Partner nav and covers them all —
// module pages consume the selection, they never grow their own pickers.
import {
	LEGACY_CLIENT_EVENT,
	OPEN_CLIENT_PICKER_EVENT,
	legacyClientId,
	loadLegacyClients,
	pickLegacyClient,
	type LegacyClient,
} from '../services/legacy-client.js';
// The library gate (navigation/library-gate.ts) is the single source of
// truth for "does this route need a chosen library", shared with app.ts's
// route-level gate so both agree. PARTNER_SIDE_PREFIXES and
// COMMUNICO_ONLY_PREFIXES live there now for the same reason.
import { COMMUNICO_ONLY_PREFIXES, PARTNER_SIDE_PREFIXES, libraryGateBlocks } from '../navigation/library-gate.js';
// Impersonation (docs/impersonation-plan.md) — "Impersonate a user…" in the
// avatar menu below + the persistent banner. Eager import: both are tiny
// and the banner has to be mounted unconditionally (like the notification
// bell) so a session started from anywhere still shows it after a route
// change.
import './cos-impersonate-dialog.js';
import './cos-impersonation-banner.js';
import {
	IMPERSONATION_EVENT,
	canImpersonate,
	currentImpersonationSession,
	stopImpersonation,
	type ImpersonationSession,
} from '../services/impersonation.js';

interface NavLink {
	href: string;
	label: string;
	/** Plain-English is the main `label`; the module brand rides as a
	    smaller subtitle beneath it (Partner workspace, 2026-07-17). */
	subtitle?: string;
	icon: string;
	/** Partner-workspace product this entry belongs to (drives the
	    product page / entitlement filter). */
	product?: PartnerProduct;
	/** The single Partner-workspace "Admin" entry. Kept as a distinct
	    flag purely so nav customization pins it out of the reorderable
	    set (permission-gated chrome, never a user-arrangeable item) —
	    it renders as a plain link like any other row. */
	adminHub?: boolean;
	/** Overrides the default href prefix-match for the active state.
	    Needed when two entries SPLIT one path prefix between them —
	    Nurture's Audience and Marketing both live under /nurture, so a
	    prefix match would light both at once (the /billing problem in
	    `_isActive`, but unfixable by an exact match because each entry
	    owns a set of sub-paths rather than just its own home). */
	activeMatch?: (path: string) => boolean;
}

// Stage 19f (audit issue 033): unicode glyphs replaced with Lucide-
// style outline icons via the cos-icon component. Single visual
// family across the nav; one place to change icon set if we swap
// libraries later.
const STAFF_NAV: NavLink[] = [
	// Stage 76 — Inbox is the new home (first); Dashboard preserved (second).
	{ href: '/inbox',          label: 'Inbox',          icon: 'inbox' },
	{ href: '/dashboard',      label: 'Dashboard',      icon: 'dashboard' },
	// Stage 48's canned dashboards + charts registry. Subtitled so it
	// reads distinctly next to /report-builder below — customers call
	// BOTH features "Reports" (the data-extract port's own legacy name),
	// so the subtitle, not the label, carries the distinction.
	{ href: '/reports',        label: 'Reports',        icon: 'table', subtitle: 'Dashboards & charts' },
	// NB /report-builder (the data-extract port) is deliberately NOT here
	// — it reports on a LIBRARY's data, so it lives in the Partner
	// workspace (PARTNER_NAV_REPORTS). The rows above report on
	// Communico's own CRM (tickets, deals, partners), which is ops.
	// Stage 42e — per-staff "My work" surface. Sits between
	// Dashboard (everyone's view) and Tickets (cross-team queue)
	// because it answers "what's on MY plate right now" — the
	// most-personal scope of the three.
	{ href: '/me/implementations', label: 'My work',    icon: 'user-check' },
	// Big Arc Sub-arc 1 — the unified task queue (personal + all record scopes).
	{ href: '/tasks',          label: 'Tasks',          icon: 'check-square' },
	// Stage 88a — personal calendar (live Google Calendar surface).
	{ href: '/calendar',       label: 'Calendar',       icon: 'calendar' },
	// Stage 61b — the personal email inbox. Sits with the personal
	// surfaces (My work) above the cross-team queues.
	{ href: '/mail',           label: 'Mail',           icon: 'mail' },
	{ href: '/conversations', label: 'Conversations', icon: 'message-circle' },
	// Stage 92c — Files (full-window Drive browser). Sits in the
	// Workspace-data cluster with Mail (Gmail) + Chat — all surfaces
	// over Google Workspace data (Drive / Gmail / Chat).
	{ href: '/files',          label: 'Files',          icon: 'folder-open' },
	// Circulate ILS staff surface (the product's workspace suite,
	// proxied through the backend's /circulation/* gateway). Rendered
	// only when the account's product set includes circulate — see
	// _navItems' product filter.
	{ href: '/circulate',      label: 'Circulate',      icon: 'book-open' },
	// Lend (Library of Things), its own product, standalone-capable
	// (Paul, 2026-09-06: supersedes the one-home-tab ruling). Shown
	// whenever the account is entitled, alongside Circulate rather than
	// folded into it.
	{ href: '/lend',           label: 'Library of Things', icon: 'package' },
	// Stage 67b — time off (Timetastic replacement). Personal surface,
	// so it sits with Mail/Chat above the cross-team queues.
	{ href: '/timeoff',        label: 'Time off',       icon: 'calendar' },
	// Training (spec 4.2): required for all Communico staff, sits directly
	// after Time off.
	{ href: '/training',       label: 'Training',       icon: 'graduation-cap' },
	{ href: '/tickets',        label: 'Tickets',        icon: 'ticket' },
	// Stage 42a — cross-partner implementation team dashboard.
	// Sits between Tickets and Projects in the nav order; the
	// surface aggregates active rollouts + active projects so
	// staff arriving at /implementation see "what needs me today".
	{ href: '/implementation', label: 'Implementation', icon: 'clipboard-list' },
	{ href: '/projects',       label: 'Projects',       icon: 'folder' },
	{ href: '/partners',       label: 'Partners',       icon: 'users' },
	// Stage 60b — Prospects (unlinked IMLS PLS libraries). Staff-only;
	// sits under Partners since a prospect is a partner-in-waiting.
	{ href: '/prospects',      label: 'Prospects',      icon: 'flag' },
	// Stage 55 — Contacts (the people at our partners). Staff-only; sits
	// between Partners and Deals since a contact is partner-attached.
	{ href: '/contacts',       label: 'Contacts',       icon: 'user' },
	// Stage 35b — Deals (spec §3.3). Staff-only; partner_nav
	// deliberately omits this entry. Sits adjacent to Partners
	// because deals are partner-attached.
	{ href: '/deals',          label: 'Deals',          icon: 'dollar-sign' },
	// Billing — the Maxio/SaaSOptics replacement. Sits after Deals because
	// that is the commercial progression: a won deal becomes a contract,
	// which bills to an account, priced from the book. Staff-level, matching
	// the RequireLevel(1) gate on every /billing route.
	// The billing HOME leads the cluster — "what needs me this morning": what
	// we're worth, what's lapsed, what renews, what moved. It used to have no
	// route at all, so the nav opened on the contracts list, which is a
	// directory rather than an answer.
	{ href: '/billing',           label: 'Billing',         icon: 'chart', subtitle: 'Overview' },
	{ href: '/billing/contracts', label: 'Contracts',       icon: 'file' },
	// Customers — the same book, rolled up by PARTNER instead of by
	// agreement: one row per customer with their contracts summed, so
	// "what is this library worth to us" doesn't mean adding up rows by
	// hand on the contracts list. 'building' — a customer is an
	// institution, not a document.
	{ href: '/billing/customers', label: 'Customers',       icon: 'building' },
	// Renewals earns its own entry rather than a link inside Contracts: 82 of
	// 376 contracts have already lapsed, and a queue nobody can find is a
	// queue nobody works. 'calendar' — a renewal is a date.
	{ href: '/billing/renewals',  label: 'Renewals',        icon: 'calendar' },
	{ href: '/billing/accounts',  label: 'Billing accounts', icon: 'dollar-sign' },
	{ href: '/billing/products',  label: 'Price book',      icon: 'table' },
	// Order forms, quotes and renewals are ONE document with three clause
	// sets, so they get one entry rather than three. 'printer' — this is the
	// surface that produces paper, and it is the only unused glyph in the set
	// that says so; Contracts and Invoices already both carry 'file'.
	{ href: '/billing/documents', label: 'Order forms',     icon: 'printer', subtitle: 'Quotes & renewals' },
	// Billing — the Maxio/SaaSOptics replacement. Sits at the end of the
	// commercial cluster (Partners → Prospects → Contacts → Deals → Billing):
	// a deal that closes becomes a contract that gets invoiced, so this is
	// the next step in that chain. Staff-only, like the rest of the cluster.
	// 'file' rather than 'dollar-sign' (Deals already owns that) — an invoice
	// is a document, and the glyph set has no receipt.
	// 'Invoices', not 'Billing' — the /billing home above now owns that word,
	// and two entries in one cluster both reading "Billing" is unusable. The
	// subtitle says what else lives here rather than repeating the label.
	{ href: '/billing/invoices', label: 'Invoices',    icon: 'file', subtitle: 'Issue & month-end close' },
	// The billing-wide settings page: invoice email template (Brief AK),
	// receipt, PDF footer, cheque bank, invoice run fence, QuickBooks and
	// reminders. One row for the whole deployment, not per-partner. It sits
	// directly under Invoices (Paul, 15 October 2026): as the last row of the
	// Billing cluster it was below the fold of the scrolling rail and could
	// not be found. The shell also scrolls the active row into view (see
	// updated()).
	{ href: '/billing/invoice-settings', label: 'Invoice settings', icon: 'mail', subtitle: 'Email, run and QuickBooks' },
	// The finance reporting surface (plan §4.7). Its own entry rather than a
	// link inside Billing: ARR/churn is what a leadership question starts
	// with, and burying it two clicks under "Invoices" is how a report stops
	// being run. 'chart' distinguishes it from the document-shaped 'file'.
	{ href: '/billing/reports',  label: 'Revenue reports', icon: 'chart', subtitle: 'ARR, MRR & churn' },
	// Cheque intake (migration 000462). Its own entry rather than a tab
	// inside Invoices: a batch of cheques arrives on its own schedule,
	// nothing else on that page is about money coming IN, and a queue
	// nobody can find is a queue nobody works. 'dollar-sign' is Billing
	// accounts' glyph and 'file' is Invoices', so this takes
	// 'credit-card' - the only glyph in the set that reads as a payment
	// instrument.
	{ href: '/billing/deposits', label: 'Cheque deposits', icon: 'credit-card', subtitle: 'Scan, record, bank' },
	// Bank activity (migration 000472). Its own entry, directly after
	// cheque deposits because that is what most of its credits turn out
	// to be, and inserted rather than placed anywhere else so no existing
	// rail row moves. 'dollar-sign' is Billing accounts' glyph and
	// 'credit-card' is the row above and 'dollar-sign' is Billing
	// accounts', so this takes 'landmark', the bank building: the page is
	// about the bank rather than about an instrument or an amount. The
	// partner rail's Boardroom uses the same glyph, which is a different
	// rail and never on screen beside this one.
	{ href: '/billing/bank-activity', label: 'Bank activity', icon: 'landmark', subtitle: 'What arrived, and what did not' },
	{ href: '/billing/reconciliation', label: 'Reconciliation', icon: 'alert-triangle', subtitle: 'COS vs Maxio' },
	{ href: '/kb',             label: 'Knowledge Base', icon: 'book-open' },
	// Community — the embedded cross-library professional network. Shown
	// only when the module is enabled for this deployment (see _navItems'
	// _communityEnabled filter); the 'share' glyph reads as a network and
	// is distinct from Partners/Roster ('users').
	{ href: '/community',      label: 'Community',      icon: 'share' },
	// Round 2 — the feedback triage console, opened to ALL Communico staff
	// (not just admins) per Paul: "chris and kieran should see this too."
	// The admin-role deep link (/admin/feedback) still resolves too.
	{ href: '/feedback',       label: 'Feedback',       icon: 'bug' },
];

// Stage 20 — admin-only nav addition. Appended after STAFF_NAV when the
// caller is in AdminRoles (mirrors backend middleware.AdminRoles).
// Points at the INDEX, not a section — /admin lands with every group
// collapsed and a "pick a category" prompt, mirroring PARTNER_NAV_ADMIN
// below. Pointing this at /admin/users (as it did until 2026-07-30) meant
// the route's own no-preselect behaviour was unreachable: clicking Admin
// jumped straight past it into Users.
const ADMIN_NAV_ITEM: NavLink = { href: '/admin', label: 'Admin', icon: 'shield' };
// Stage 49 — super_admin sits ABOVE admin and must keep every admin surface.
const ADMIN_ROLES = new Set(['admin', 'executive', 'super_admin']);

// ── Workspace split (2026-07-08, Paul) ─────────────────────────────────
// ONE sidebar, TWO workspaces, switched by the tab pair at the top of the
// nav: "Communico" (the internal ops platform — everything COS is today)
// and "Partner" (the products libraries buy: Circulate/Lend now, the
// other modules as they arrive). The switcher is NAV STATE, not a
// permission boundary — server-side scope stays the only access control.
// Staff always LAND on Communico (no persistence, per Paul); deep links
// to a partner-side route flip the tab automatically. Future library-
// staff logins (Phase B) get the Partner workspace only.
type Workspace = 'communico' | 'partner';

// Paths that live on the Partner side. /kb is SHARED (both workspaces
// link it; the partner side filters to partner-visible articles via
// ?view=partner), so it never forces a switch by itself.
// /desk, /action-center and /communications are Partner-only too (they
// have no STAFF_NAV entry — only PARTNER_NAV_DESK/_ACTION_CENTER/_COMMS
// and the level-3 partnerNav() carry them) but were missing here, so
// navigating to any of them from the Partner tab fell through to the
// `else` branch below and silently flipped back to Communico (feedback
// report: clicking Action Center from the partner side bounced you
// back to the Communico staff side).
// '/measure' stays listed alongside '/thrive' - its routes now redirect
// (app.ts), but the workspace check must not flip back to Communico for the
// one frame a redirect is in flight.
// (imported from navigation/library-gate.js, above, kept alongside app.ts's
// route-level library gate so both read the exact same list.)

// Paul, 2026-08-18: impersonation is scoped to the Partner workspace —
// "you should only be able to impersonate on the partner page ... if you
// navigate away you lose the session and revert back to your normal log
// in". These are the Communico-staff-only surfaces: reaching one while a
// session is live ends it rather than leaving a library user's identity
// attached to a request that was never part of their product.
//
// Deliberately NOT the inverse of PARTNER_SIDE_PREFIXES. A library user's
// own nav includes /dashboard, /tickets, /kb and friends, so treating
// "not partner-side" as "navigated away" would end the session on the
// very first screen after starting it.
// (also imported from navigation/library-gate.js, above.)

// The Partner workspace's product entries (filtered by entitlement in
// _navItems, Lend is its own product now, always shown when entitled,
// directly after Circulate/Library Management System).
// Each product row renders ONLY the plain-English function label (Paul,
// 2026-08-18: "remove the module names from the navigation — not
// relevant to the user"). The Communico brand names live on in
// PARTNER_MODULE_LABELS.sub, which still feeds the /partner-admin page
// titles — the rail just no longer surfaces them.
const mod = (href: string, icon: string, product: PartnerProduct): NavLink => ({
	href,
	icon,
	product,
	label: PARTNER_MODULE_LABELS[product].main,
});
const PARTNER_NAV_CIRCULATE = mod('/circulate', 'book-open', 'circulate');
const PARTNER_NAV_LEND = mod('/lend', 'package', 'lend');
// Outreach: ONE rail entry covering both Visits (booked internal/
// external visits) and Bookmobile (the pre-existing vehicles/stops/
// routes surface). Its href is the section landing page a bare click
// resolves to; the two sections' own tab bars (outreach/nav.ts) carry
// the rest of the navigation once inside it.
// The rail row lights up for the WHOLE section, not just its href.
// Without this the row goes dark the moment you switch to the Bookmobile
// tab, because the href is the Visits landing page and the default
// matcher is a prefix test against that href. /bookmobile is the
// pre-rename URL, live for one frame before app.ts redirects it.
const PARTNER_NAV_OUTREACH: NavLink = {
	...mod('/outreach/visits', 'map', 'outreach'),
	activeMatch: (path) => path.startsWith('/outreach') || path.startsWith('/bookmobile'),
};
const PARTNER_NAV_PRESERVE = mod('/preserve', 'archive', 'preserve');
const PARTNER_NAV_ROSTER = mod('/roster', 'users', 'roster');
// Thrive replaces Measure in place (Paul, 2026-09-06 - same rail slot, same
// pinned position below; Measure's routes now redirect, see app.ts). Icon
// stays 'chart' for now (the plan doc: "icon 'chart' for now").
const PARTNER_NAV_THRIVE = mod('/thrive', 'chart', 'thrive');
// Nurture is ONE purchased product presenting as SEVERAL rail entries
// (Paul, 2026-08-14 onward): Audience is every person the library
// knows, Marketing is the email programme. Both are pushed on the
// `nurture` entitlement at the call site, and both deliberately OMIT
// the `product` field — that field is the nav-customization key
// (`_partnerReorderKey`), so rows sharing 'nurture' would collide into
// one reorderable/hideable item. Without it the key falls back to the
// href, so a library can order and hide each entry independently (the
// Comms/Media/Desk precedent, which are likewise gated at the push site
// rather than by the field).
// The three remaining Marketing surfaces, the Boardroom (Paul,
// 2026-08-14: its own rail link rather than a tab under Audience — a
// board is answerable to the public, not a slice of the patron list),
// Email (Paul, 2026-08-18: the inbound rail's surface is its own rail
// entry with its own per-inbox tabs, not an Audience tab), and Surveys
// (Paul, 2026-08-20: promoted out of Marketing's tab set into its own
// rail entry, seated directly after Marketing — its route stays under
// /nurture/surveys, the Email promotion's own precedent). Everything
// else under /nurture — the people list, a person record,
// organisations, the work queue — is Audience, so Audience matches the
// prefix MINUS these sets rather than listing its own (a new Audience
// surface then needs no nav change, while a new Marketing/Board/Email/
// Surveys surface must be added here, which is the right way round:
// those are the closed sets).
const NURTURE_MARKETING_PREFIXES = ['/nurture/segments', '/nurture/emails', '/nurture/campaigns', '/nurture/journeys'];
const isNurtureMarketingPath = (path: string): boolean => NURTURE_MARKETING_PREFIXES.some((p) => path.startsWith(p));
const NURTURE_BOARD_PREFIX = '/nurture/board';
const isNurtureBoardPath = (path: string): boolean => path.startsWith(NURTURE_BOARD_PREFIX);
const NURTURE_EMAIL_PREFIX = '/nurture/inbound-email';
const isNurtureEmailPath = (path: string): boolean => path.startsWith(NURTURE_EMAIL_PREFIX);
// Surveys — promoted out of Marketing's tab set into its own rail entry
// (Paul, 2026-08-20), seated directly after Marketing. Its route stays
// /nurture/surveys (the list) + /nurture/surveys/:sid (the workspace) +
// /nurture/surveys/new (a fresh draft) — the Email promotion's own
// precedent of keeping the existing route home.
const NURTURE_SURVEYS_PREFIX = '/nurture/surveys';
const isNurtureSurveysPath = (path: string): boolean => path.startsWith(NURTURE_SURVEYS_PREFIX);
const PARTNER_NAV_AUDIENCE: NavLink = {
	href: '/nurture',
	icon: 'heart',
	label: 'Contacts',
	activeMatch: (path) =>
		path.startsWith('/nurture') &&
		!isNurtureMarketingPath(path) &&
		!isNurtureBoardPath(path) &&
		!isNurtureEmailPath(path) &&
		!isNurtureSurveysPath(path),
};
const PARTNER_NAV_MARKETING: NavLink = {
	href: '/nurture/segments',
	icon: 'megaphone',
	label: 'Marketing',
	activeMatch: isNurtureMarketingPath,
};
// Surveys' own rail entry (Paul, 2026-08-20) — seated directly after
// Marketing in the pinned order (see _navItems' push site below).
// Deliberately omits `product` for the same nav-customization-key
// reason as Audience/Marketing/Boardroom/Email.
const PARTNER_NAV_SURVEYS: NavLink = {
	href: NURTURE_SURVEYS_PREFIX,
	icon: 'list-checks',
	label: 'Surveys',
	activeMatch: isNurtureSurveysPath,
};
const PARTNER_NAV_BOARDROOM: NavLink = {
	href: NURTURE_BOARD_PREFIX,
	icon: 'landmark',
	label: 'Boardroom',
	activeMatch: isNurtureBoardPath,
};
// Email — the library's shared inboxes (the §9.3 inbound rail's staff
// surface). Its own rail entry per Paul (2026-08-18): a daily-drive
// surface with its own per-inbox tab structure, so it cannot live as an
// Audience tab. Like Audience/Marketing/Boardroom it deliberately omits
// the `product` field (the nav-customization key would collide four
// Nurture rows into one reorderable item); entitlement gates it at the
// push site.
const PARTNER_NAV_EMAIL: NavLink = {
	href: NURTURE_EMAIL_PREFIX,
	icon: 'inbox',
	// "Shared email", not "Inbox" or "Email" (Paul, 2026-10-29). The COMMUNICO
	// rail has its own '/inbox' entry (a person's own work list) labelled
	// "Inbox", so this shared mailbox needed a name that cannot be confused
	// with it.
	label: 'Shared email',
	activeMatch: isNurtureEmailPath,
};
const PARTNER_NAV_BROADCAST = mod('/broadcast', 'monitor', 'broadcast');
const PARTNER_NAV_ATTEND = mod('/attend', 'calendar', 'attend');
// 'table' rather than 'calendar' — Attend already owns the calendar glyph,
// and a room with a table is the nearest thing the icon set has to a
// bookable space.
const PARTNER_NAV_RESERVE = mod('/reserve', 'table', 'reserve');
// Appointments (the Schedule product), pinned directly after Reserve. The
// calendar glyph is taken by Attend, so this uses 'clock'.
const PARTNER_NAV_SCHEDULE = mod('/schedule', 'clock', 'schedule');
// All partner-module admin lives under ONE "Admin" prime link (2026-07-17,
// Paul), which opens /partner-admin — that page's own accordion nav lists
// every entitled module (2026-07-28), so the rail link no longer carries a
// module-picker flyout (retired 2026-07-29: the flyout existed only because
// /partner-admin used to show one module at a time). Community Info (whose
// only surface IS its admin — the patron side is Create blocks on Explore)
// folds in there rather than carrying its own product row.
// Desk (docs/desk-plan.md) — the front-of-house surface: scan or search,
// get the person, do the thing. Like Communications/Media below, it is
// deliberately NOT entitlement-gated (a desk is not a purchased product —
// its own task lanes derive their entitlement from /circulate/context);
// placed first because it's the screen a staffer has open all day, ahead
// of even Action Center's "what's waiting on you" queue.
const PARTNER_NAV_DESK: NavLink = { href: '/desk', label: 'Desk', icon: 'search' };
// Action Center (docs/action-center-plan.md) — the merged approvals queue
// over Attend + Reserve. Not a purchased module of its own (no `product`
// field, like Comms/Media below), so it's gated at the push site on either
// of the two products it queues being entitled, rather than through the
// mod()/PartnerProduct machinery. 'list' — a queue of things to act on.
const PARTNER_NAV_ACTION_CENTER: NavLink = { href: '/action-center', label: 'Action Center', icon: 'list' };
// Communications is the platform's delivery rail, not a purchased
// module — every library gets it whatever else they bought, so it is
// deliberately NOT entitlement-filtered like the product rows above.
// No brand subtitle either: there is no product name to carry.
const PARTNER_NAV_COMMS: NavLink = { href: '/communications', label: 'Communications', icon: 'mail' };
// Media is likewise NOT entitlement-gated: it is the substrate under
// whichever products a library bought, not a product of its own.
const PARTNER_NAV_MEDIA: NavLink = { href: '/media', label: 'Media', icon: 'image' };
// Collections (Explore-carousel manager port, docs/collections-cos-
// migration-prd.md) is likewise NOT entitlement-gated via the
// CIRCULATE_PRODUCTS lever: it is a free Explore feature governed by
// the legacy `access['collections'] OR level<=3` check, not a
// purchased module — every library that has Explore can use it. The
// page's own per-client GET /collections/context call is the real
// gate.
const PARTNER_NAV_COLLECTIONS: NavLink = { href: '/collections', label: 'Collections', icon: 'square' };
// Explore admin (docs/modules/explore.md) - search tuning, catalogue
// curation and MARC import status. Its own rail entry (Paul, 2026-09-07),
// seated directly after Collections because both are Explore surfaces:
// Collections merchandises the catalogue, Explore repairs the index
// behind it. Deliberately NOT entitlement-gated through CIRCULATE_PRODUCTS
// for the same reason Collections is not: it configures a library's own
// Explore, which every Explore library has, rather than a purchased
// module. Like Collections, it omits `product` so the nav-customization
// key falls back to the href.
const PARTNER_NAV_EXPLORE: NavLink = {
	href: '/explore',
	label: 'Explore',
	// 'compass', not 'search': Desk already owns the search glyph, and a
	// compass reads as finding your way through a catalogue rather than as
	// a query box.
	icon: 'compass',
	subtitle: 'Catalogue search',
	activeMatch: (path) => path.startsWith('/explore'),
};
// Reports (data extracts) — the port of the legacy control-panel
// "Reports" system. It extracts a LIBRARY's own data, so it belongs on
// the Partner side, not in Communico ops. Not entitlement-gated: every
// client had Reports in the old control panel, so it is a platform
// surface like Communications and Media rather than a purchased module.
// No brand subtitle — there is no product name to carry; "Data
// extracts" says what it does. The /report-builder URL is a permanent
// CTO decision (/reports belongs to Measure's dashboards), which is
// exactly why the LABEL is what customers call it.
const PARTNER_NAV_REPORTS: NavLink = {
	href: '/report-builder',
	label: 'Reports',
	icon: 'file-spreadsheet',
	subtitle: 'Data extracts',
};
// Annual report: the federal Public Libraries Survey, filed from the
// library's own data. It is a Thrive/Measure feature (the engine reads the
// warehouse-side counts), so the row shows for every library (Measure is
// every library's). It has its own route because the
// staff Reports tab it also lives in (/report-builder) is level 1 only; the
// API underneath is RequireLevelScoped(3).
const PARTNER_NAV_ANNUAL_REPORT: NavLink = {
	href: '/annual-report',
	label: 'Annual report',
	icon: 'file-spreadsheet',
	subtitle: 'Public Libraries Survey',
};
// Community — the cross-library professional network. Not a purchased
// product (no `product` field, pushed like Comms/Media); shown when the
// module is enabled for the deployment (_communityEnabled). ?view=partner
// keeps the Partner workspace tab active (the path is workspace-ambiguous,
// like /kb — it is NOT in PARTNER_SIDE_PREFIXES).
const PARTNER_NAV_COMMUNITY: NavLink = { href: '/community?view=partner', label: 'Community', icon: 'share' };
const PARTNER_NAV_ADMIN: NavLink = { href: '/partner-admin', label: 'Admin', icon: 'settings', adminHub: true };
const PARTNER_NAV_KB: NavLink = { href: '/kb?view=partner', label: 'Knowledge Base', icon: 'file' };

// What a LIBRARY login (level 3 and 4) may see on its rail, from the Partner
// workspace's pinned rows. This is not "what the library bought" (that is
// /circulate/context's list); it is the subset whose routes admit a library
// tier today, so a row is never a dead link. Derived from router.go on
// 4 October 2026:
//   products: Circulate and Lend ride authMiddleware-only proxies, Reserve is
//   /library/reserve and Attend is /attend (RequireLevelScoped), Outreach,
//   Roster and Thrive/Measure are RequireLevelScoped(3).
//   hrefs: Collections (/collections, router.go:3961) and Explore (/explore,
//   router.go:4007) are RequireLevelScoped(4) groups with the tenant pinned
//   from the token. They are not products, so they are matched by href.
//   adminHrefs: the Boardroom (/nurture/board), opened to a library's own
//   admins at levels 2 and 3 on 31 October 2026 (Paul, Boardroom fix plan
//   Part 5): its staff routes admit levels 1 to 3, with a library login pinned
//   to its own library. Level 4 waits for per-user permissions, so it is
//   matched separately from `hrefs`, which every library login sees.
//   adminProducts: the same admin-tier rule for rows that ARE a product,
//   because adminHrefs only matches rows without one. Preserve (Digital
//   Archives) was opened to a library's own admins at levels 2 and 3 on
//   1 November 2026 (Paul, Preserve Decision 2): its staff routes admit them,
//   pinned to their own library, and refuse a library without the Preserve
//   subscription. Level 4 waits for per-user permissions. The row itself
//   still only appears when the library's product list includes Preserve.
// Left out on purpose: Broadcast (RequireLevel(1)) and the rest of Nurture
// (Contacts, Marketing, Surveys, Email: RequireLevel(1) or admin). The owner
// has deferred opening those; the access-gates work changes this ONE
// constant when it does.
export const LIBRARY_RAIL_ALLOW: {
	products: ReadonlySet<PartnerProduct>;
	hrefs: ReadonlySet<string>;
	adminHrefs: ReadonlySet<string>;
	adminProducts: ReadonlySet<PartnerProduct>;
} = {
	products: new Set<PartnerProduct>(['circulate', 'lend', 'reserve', 'schedule', 'attend', 'outreach', 'roster', 'thrive']),
	hrefs: new Set(['/collections', '/explore', '/annual-report']),
	adminHrefs: new Set(['/nurture/board']),
	adminProducts: new Set<PartnerProduct>(['preserve']),
};

// Every rail item the app can render, in one array, purely so the breadcrumb
// trail can look a section's label and icon up at RENDER time
// (docs/design-system/breadcrumb-trail.md §9: those strings are deliberately
// NOT copied into navigation/trail-registry.ts, which stores only the href).
// _trailSections() puts the user's OWN nav first, so a workspace's or a
// tenant's label always wins; this array is the fallback for a section that
// is not on their rail — a staff deep link into a partner surface, or an
// admin page reached from a URL.
export const ALL_RAIL_ITEMS: NavLink[] = [
	...STAFF_NAV,
	ADMIN_NAV_ITEM,
	PARTNER_NAV_CIRCULATE, PARTNER_NAV_LEND, PARTNER_NAV_OUTREACH, PARTNER_NAV_PRESERVE,
	PARTNER_NAV_ROSTER, PARTNER_NAV_THRIVE, PARTNER_NAV_AUDIENCE, PARTNER_NAV_MARKETING,
	PARTNER_NAV_SURVEYS, PARTNER_NAV_BOARDROOM, PARTNER_NAV_EMAIL, PARTNER_NAV_BROADCAST,
	PARTNER_NAV_ATTEND, PARTNER_NAV_RESERVE, PARTNER_NAV_SCHEDULE, PARTNER_NAV_DESK, PARTNER_NAV_ACTION_CENTER,
	PARTNER_NAV_COMMS, PARTNER_NAV_MEDIA, PARTNER_NAV_COLLECTIONS, PARTNER_NAV_EXPLORE, PARTNER_NAV_REPORTS,
	PARTNER_NAV_ANNUAL_REPORT,
	PARTNER_NAV_COMMUNITY, PARTNER_NAV_ADMIN, PARTNER_NAV_KB,
];

// The admin settings surfaces now live in the grouped, filterable
// secondary sidebar (cos-admin-nav). The shell only decides WHEN to
// show it (path starts with /admin) and lays it out beside the page.

// Partner-admin (level 3) nav. No projects (internal-only) and the
// Partners link is rewritten to point at their own library.
function partnerNav(partnerId: number | null): NavLink[] {
	const myLib = partnerId ? `/partners/${partnerId}` : '/dashboard';
	return [
		{ href: '/dashboard',      label: 'Dashboard',      icon: 'dashboard' },
		// Desk (docs/desk-plan.md) — reachable from the level-3 shell same
		// as the Partner workspace's copy; library staff are its intended
		// user, and it's the screen they have open all day.
		{ href: '/desk',           label: 'Desk',           icon: 'search' },
		// Action Center — reachable from the level-3 shell same as the
		// Partner workspace's copy; library admins are its primary users
		// (docs/action-center-plan.md).
		{ href: '/action-center',  label: 'Action Center',  icon: 'list' },
		{ href: '/tickets',        label: 'Tickets',        icon: 'ticket' },
		{ href: '/implementation', label: 'Implementation', icon: 'clipboard-list' },
		{ href: '/kb',             label: 'Knowledge Base', icon: 'book-open' },
		{ href: myLib,             label: 'My Library',     icon: 'users' },
		// The desk lookup's intended user is library staff, so it has to
		// be reachable from the level-3 shell as well as the Partner
		// workspace. (This list is the legacy partnerNav — the wider
		// refactor onto the workspace model is its own job.)
		{ href: '/communications', label: 'Communications', icon: 'mail' },
		// Reports (data extracts) — library staff are the people who
		// actually run these, so it has to be reachable here too. Their
		// client comes from their token, so they never need the picker.
		{ href: '/report-builder', label: 'Reports', icon: 'file-spreadsheet', subtitle: 'Data extracts' },
	];
}

// ── In-app help (module-agnostic cos-help-panel, mounted ONCE for every
// authenticated route) ──────────────────────────────────────────────────
// A single shared panel replaces what used to be four separate per-page
// mounts (Circulate/Roster/Attend/Attend-editor each imported + rendered
// their own copy of <cos-help-panel>). See cos-help-panel.ts's class doc
// for the engine + adoption recipe, and _renderHelp()/_mountHelp() below
// for how the shell wires it up.
//
// KNOWN_HELP_MODULES is the set of module ids helpgen has (or is
// publishing) content for — see backend/cmd/helpgen/modules/*.json, whose
// file basenames ARE the /help/<module>/ directory names this list
// mirrors. A route that resolves to a module NOT in this set renders the
// panel's designed "no help here yet" state (cos-help-panel's `available`
// prop) instead of attempting a fetch that would just 404.
export const KNOWN_HELP_MODULES = new Set([
	'action-center',
	'activities',
	'activity',
	'admin',
	'attend',
	'billing',
	'broadcast',
	'calendar',
	'circulate',
	'collections',
	'comms',
	'community',
	'contacts',
	'conversations',
	'dashboard',
	'deals',
	'desk',
	'email-studio',
	'explore',
	'feedback',
	'files',
	'implementation',
	'inbox',
	'kb',
	'lend',
	'mail',
	'me',
	'measure',
	'media',
	'notifications',
	'nurture',
	'outreach',
	'partner-admin',
	'partners',
	'peer-comparison',
	'preserve',
	'projects',
	'prospects',
	'report-builder',
	'reports',
	'reserve',
	'roster',
	'schedule',
	'settings',
	'tasks',
	'thrive',
	'tickets',
	'timeoff',
	'training',
	'triage-review',
]);

// Ordered path-prefix → module-id table (first prefix a path starts with
// wins). Route-shaped knowledge the SHELL alone needs — cos-help-panel.ts
// only ever sees the already-resolved module id, never a path.
export const HELP_MODULE_ROUTES: ReadonlyArray<readonly [string, string]> = [
	['/admin/triage-review', 'triage-review'],
	['/admin/feedback', 'feedback'],
	['/admin/training', 'training'],
	['/admin', 'admin'],
	['/billing', 'billing'],
	['/deals', 'deals'],
	['/action-center', 'action-center'],
	['/dashboards', 'dashboard'],
	['/dashboard', 'dashboard'],
	// '/activities' (the CRM activity-report drill-through list) never
	// collides with '/activity' (the staff self-serve usage-analytics page,
	// teamView off): the prefix-match requires an exact match or a '/'
	// right after the prefix, and '/activities' has no '/' at that
	// position against the '/activity' prefix, so neither is a
	// path-prefix of the other regardless of table order.
	['/activities', 'activities'],
	['/activity', 'activity'],
	['/peer-comparison', 'peer-comparison'],
	// '/report-builder' never collides with a future '/reports' entry (the
	// partner-facing dashboards module, not yet in this table): neither is
	// a path-prefix of the other, so this line's position doesn't matter,
	// but it stays ahead of the general block on principle.
	['/report-builder', 'report-builder'],
	['/mail', 'mail'],
	['/conversations', 'conversations'],
	['/communications', 'comms'],
	['/partner-admin', 'partner-admin'],
	['/partners', 'partners'],
	['/prospects', 'prospects'],
	['/contacts', 'contacts'],
	['/projects', 'projects'],
	['/timeoff', 'timeoff'],
	['/feedback', 'feedback'],
	['/me', 'me'],
	['/circulate', 'circulate'],
	['/roster', 'roster'],
	['/attend', 'attend'],
	['/reserve', 'reserve'],
	['/schedule', 'schedule'],
	['/lend', 'lend'],
	['/media', 'media'],
	['/preserve', 'preserve'],
	['/thrive', 'thrive'],
	['/measure', 'measure'],
	['/community', 'community'],
	['/desk', 'desk'],
	['/broadcast', 'broadcast'],
	['/collections', 'collections'],
	['/explore', 'explore'],
	['/files', 'files'],
	['/inbox', 'inbox'],
	['/notifications', 'notifications'],
	// An agent's approval page is reached from a Slack card or a notification.
	['/agent-approvals', 'notifications'],
	// So is a person's repeating-tasks page (from its Slack receipt).
	['/agent-schedules', 'notifications'],
	// And the missions page (from a mission's Slack status message).
	['/agent-missions', 'notifications'],
	// And workflow runs (from a run's link).
	['/workflow-inbox', 'notifications'],
	['/workflow-runs', 'notifications'],
	['/settings', 'settings'],
	['/tasks', 'tasks'],
	['/calendar', 'calendar'],
	['/nurture', 'nurture'],
	// fleet/training-ui - the admin board's own path doesn't share the
	// /training prefix, so it needs its own entry into the same module.
	// It sits in the /admin group above, not here: first match wins, so an
	// entry below ['/admin', 'admin'] could never resolve.
	['/training', 'training'],
	['/reports', 'reports'],
	['/annual-report', 'reports'],
	['/kb', 'kb'],
	['/outreach', 'outreach'],
	['/bookmobile', 'outreach'],
	['/implementation', 'implementation'],
	['/tickets', 'tickets'],
];
// The one route shaped differently than its parent module's prefix: the
// Email Studio editor lives under /nurture/campaigns/:id/studio but is
// its own helpgen module (email-studio.json), not "nurture" — must be
// checked before the /nurture prefix above.
const EMAIL_STUDIO_HELP_PATH = /^\/nurture\/campaigns\/\d+\/studio\/?$/;

export function helpModuleForPath(path: string): string {
	if (EMAIL_STUDIO_HELP_PATH.test(path)) return 'email-studio';
	for (const [prefix, module] of HELP_MODULE_ROUTES) {
		if (path === prefix || path.startsWith(`${prefix}/`)) return module;
	}
	return '';
}

// Per-module screenLabels/screenNavHooks (cos-help-panel.ts's properties
// of the same name) — only for modules whose content declares screen/nav
// keys; ported verbatim from what cos-roster-page.ts / cos-attend-page.ts
// used to pass directly. Every other module gets the base panel's empty
// defaults. Circulate is deliberately NOT here: it self-configures inside
// cos-circulate-help.ts's constructor (the subclass _renderHelp mounts in
// its place below), which also owns its own copy/consent-prompt text and
// its `active-module`-gated availability.
const HELP_SCREEN_CONFIG: Record<string, { labels: Record<string, string>; navHooks: Record<string, string> }> = {
	roster: {
		// The roster-rethink shell's seven spaces (roster-shell/spaces.ts)
		// plus the worker landing (rs-worker.ts, not part of the space nav
		// but still a help "screen"). Replaces the eleven-tab strip's
		// labels/navHooks, which the 2026-08-29 retirement deleted —
		// this map went stale silently until then, exactly the failure
		// mode check-help-anchors.mjs now catches at build time.
		labels: {
			home: 'Home', today: 'Today', plan: 'Plan', publish: 'Publish & notify',
			approvals: 'Approvals', people: 'People', insights: 'Insights', worker: 'Your shifts',
		},
		navHooks: {
			home: 'nav.space.home', today: 'nav.space.today', plan: 'nav.space.plan', publish: 'nav.space.publish',
			approvals: 'nav.space.approvals', people: 'nav.space.people', insights: 'nav.space.insights',
		},
	},
	attend: {
		labels: { events: 'Events', templates: 'Templates' },
		navHooks: { events: 'page.tab.events', templates: 'page.tab.templates' },
	},
};
const EMPTY_HELP_MAP: Record<string, string> = {};

// A route whose help is one guide inside a bigger module opens the panel on
// that guide rather than the module's list (the Boardroom lives in Nurture's
// help, where it was only reachable by searching "board").
//
// Outreach: each screen opens its own article. First match wins, so the
// longer Bookmobile paths come before /outreach/bookmobile itself, which is
// the Vehicles screen. The visit detail page shares the Visits article.
// Thrive's Outreach page opens the Operations article that covers it.
const HELP_START_PAGES: ReadonlyArray<readonly [string, string]> = [
	['/nurture/board', 'board'],
	['/outreach/bookmobile/stops', 'bookmobile-stops'],
	['/outreach/bookmobile/routes', 'bookmobile-routes'],
	['/outreach/bookmobile/trips', 'bookmobile-trips'],
	// The Public timetable tab is explained in the Routes article, beside the website switch.
	['/outreach/bookmobile/public-timetable', 'bookmobile-routes'],
	['/outreach/bookmobile', 'bookmobile-vehicles'],
	['/outreach/visits', 'visits'],
	['/thrive/operations/outreach', 'operations'],
];

export function helpStartPageForPath(path: string): string {
	for (const [prefix, page] of HELP_START_PAGES) {
		if (path === prefix || path.startsWith(`${prefix}/`)) return page;
	}
	return '';
}

/**
 * <cos-app-shell> — header + sidebar chrome wrapping every authenticated
 * route. The active page slots into the main region.
 *
 * Above 768px: persistent sidebar in a CSS grid.
 * Below 768px (Stage 15 fix): hamburger button in the header reveals
 * the same sidebar as a slide-in overlay with backdrop. Focus is
 * trapped inside the overlay while open and restored to the hamburger
 * when closed. Escape closes; backdrop click closes; nav-item click
 * closes (then navigates). Without this, mobile users had no way to
 * navigate — the old `nav { display: none }` rule hid the only menu.
 */
@customElement('cos-app-shell')
export class CosAppShell extends LitElement {
	@property({ type: String }) path = '/dashboard';
	@property({ type: String }) userEmail = '';
	@property({ type: String }) userName = '';
	@property({ type: Number }) userLevel = 1;
	@property({ type: Number }) userPartnerId: number | null = null;
	// The re-test link's "come back to the report" leg. Delivered by app.ts
	// as a property (never re-derived from the URL here — the URL has
	// already been stripped by the time this arrives; see app.ts's
	// _captureReportFocusFromURL for why that has to happen earlier, inside
	// the route guard). A fresh object every time, deliberately — see that
	// same header comment for why a repeated bare number wouldn't register
	// as a change under Lit's default diffing.
	@property({ attribute: false }) pendingReportFocus: { id: number } | null = null;
	// The library's word for the people it serves, as a plural. Only read
	// so a change redraws the nav labels built from activeCardholderTerm().
	@property({ attribute: false }) cardholderPlural = '';
	// 65a-4 — own user id (status frames + self-notification suppression).
	@property({ type: Number }) userId = 0;
	// Stage 20 — drives the admin nav row. Mirrors the backend role
	// gate; an empty string means "no admin link". Only adds the entry
	// to STAFF_NAV (level-3 partner users never see it).
	@property({ type: String }) userRole = '';
	// RequireBillingAccess replaced the level-1 billing gate with a
	// hardcoded named allowlist; app.ts computes this from /auth/me's
	// can_access_billing flag (fail-closed, defaults false) so the rail
	// never lights a link the route would only 403.
	@property({ type: Boolean }) canAccessBilling = false;

	@state() private _navOpen = false;
	// Which side of the workspace split the nav shows. Always starts on
	// Communico; route-synced in willUpdate (a partner-side deep link
	// flips it, a communico-side navigation flips it back).
	@state() private _workspace: Workspace = 'communico';
	// Collapsible desktop sidebar (icon-only rail). Reflected to the host
	// so :host([collapsed]) can swap the grid column width. Persisted per
	// device in localStorage; mobile (<=768px) ignores it and always
	// shows the full-label overlay.
	@property({ type: Boolean, reflect: true }) collapsed = false;
	// True only while the rail is collapsed because a docked side panel
	// BORROWED the space (not because the user chose it). Deliberately not
	// persisted: a borrowed collapse must never become the saved preference,
	// and it's cleared the moment the user takes the toggle back.
	private _navBorrowed = false;
	// 65a-3b — total unread chat messages (nav badge). Synced by the
	// /chat page when open; refetched (debounced) on live frames.
	@state() private _chatUnread = 0;
	// Action Center nav badge (docs/action-center-plan.md) — unread_total
	// under the default filters, polled every 60s while the row is visible
	// (level 3+, or level<3 in the Partner workspace) + refreshed on the
	// page's own cos:action-center-changed event so an action taken there
	// clears the badge immediately rather than waiting out the poll.
	@state() private _acUnread = 0;
	private _acPollTimer: ReturnType<typeof setInterval> | null = null;
	// Circulate-hosted product entitlements ('circulate' / 'lend') —
	// drives the Circulate/Lend nav entries. One cached fetch per
	// session via services/circulate-products.
	@state() private _circProducts: string[] = [];
	// Whether the Community module is enabled for this deployment (drives
	// the three Community nav rows). Deployment-level, not per-account, so
	// it's loaded for every user level (unlike _circProducts). One cached
	// fetch per session via services/community-context.
	@state() private _communityEnabled = false;
	// Partner-workspace nav customization: the saved per-user layout doc
	// (null = default order) + whether the Customize/edit mode is on. The
	// dragged item key lives outside @state (a transient DnD cursor).
	@state() private _navLayout: NavLayoutDoc | null = null;
	@state() private _navEdit = false;
	private _navDragKey: string | null = null;
	// Live drop-indicator state (a brand line before/after the hovered
	// row, or a highlighted target section). Plain fields + explicit
	// requestUpdate — dragover fires rapidly, so we re-render only on a
	// real position change.
	private _navDropKey: string | null = null;
	private _navDropAfter = false;
	private _navDropSection: number | null = null;
	// Shared legacy-client picker (Partner nav top; control-panel-ported
	// modules). Loaded once for level-1 staff with a client-scoped
	// module entitled; selection flows through services/legacy-client.
	@state() private _legacyClients: LegacyClient[] = [];
	@state() private _legacyClientId = 0;
	/* Client-picker typeahead. _clientQuery null = not searching, so the
	   input shows the chosen client's name rather than a stale search. */
	@state() private _clientQuery: string | null = null;
	@state() private _clientOpen = false;
	@state() private _clientActive = 0;
	// 65a-4 — own platform-wide status ('' = Active) + menu open flag.
	@state() private _myStatus = '';
	@state() private _statusOpen = false;
	// Stage 85a — user avatar dropdown (Settings + Sign out).
	@state() private _userMenuOpen = false;
	// Impersonation (docs/impersonation-plan.md). Whether the SIGNED-IN
	// staff user holds the grant (D2 — an explicit per-user capability,
	// never inferred from level=1 alone); fetched once for level-1 staff,
	// mirroring _communityEnabled/_circProducts above. The live session
	// itself is read from services/impersonation.ts (the same source the
	// banner and the header-injection in api.ts consult) rather than
	// tracked independently here, so the menu and the banner can never
	// disagree about whether one is active.
	@state() private _canImpersonate = false;
	@state() private _impersonation: ImpersonationSession | null = null;
	@state() private _impersonateDialogOpen = false;
	@state() private _stoppingImpersonation = false;
	// conversation id -> notify level + display meta (native-notification
	// gating; refreshed with every unread refetch).
	private _notifyMeta = new Map<number, { level: string; name: string; kind: string }>();
	private _statusHydrated = false;
	private _chatUnreadTimer: ReturnType<typeof setTimeout> | null = null;
	// Responsive Workstream A2 — the shared ViewportController (was a raw
	// window.matchMedia('(max-width: 768px)') + manual listener). `_isNarrow`
	// is now a plain getter over the controller's `.tier` — the controller's
	// own hostConnected/hostDisconnected own the matchMedia listeners, and its
	// requestUpdate() call on a tier change is what triggers this element's
	// re-render (same reactivity the old @state field gave, one fewer thing
	// to keep in sync by hand). Drives the `inert` attribute on the closed
	// nav so screen readers + keyboard users don't tab into off-screen links.
	private _viewport = new ViewportController(this);
	private get _isNarrow(): boolean {
		return this._viewport.tier === 'compact';
	}
	// Plain (non-reactive) — compared against the live getter in updated() to
	// detect a narrow→wide transition, replacing the old matchMedia 'change'
	// listener's auto-close (see updated()).
	private _wasNarrow = false;

	// ── The breadcrumb trail (docs/design-system/breadcrumb-trail.md) ─────
	// The record page's own label, pushed here by services/trail.ts. Cleared
	// on every route change so a stale name can never survive into the next
	// page.
	// Bumped whenever services/trail.ts publishes anything. The VALUES are
	// read fresh at render time and filtered to the current route
	// (_trailLeaf / _trailCrumbLabels below), because a page's last render can
	// land after the route already changed; this field exists only to make
	// that re-render happen.
	@state() private _trailRevision = 0;
	// True between arriving on a record route and either the leaf landing or
	// the 3s deadline passing; drives cos-trail's leaf skeleton, after which
	// the registry's fallback label shows instead.
	@state() private _leafPending = false;
	// Tracked separately from `path` because a same-path QUERY change
	// (/circulate?m=cataloging) never updates `path`, and the trail's leaf
	// can come from the query.
	@state() private _trailSearch = '';
	private _unsubscribeLeaf: (() => void) | null = null;
	private _leafTimer: ReturnType<typeof setTimeout> | null = null;

	// Stage 30 — admin agent panel state. _agentOpen drives the
	// slide-over `open` attribute (CSS-only show/hide once mounted).
	// _agentMounted flips true on first FAB click after the dynamic
	// import resolves; we don't ship the panel code in the initial
	// bundle. Subsequent toggles just flip _agentOpen.
	@state() private _agentOpen = false;
	@state() private _agentMounted = false;

	// In-app help — one shared launcher/panel for every authenticated route
	// (was 4 separate per-page mounts). Unlike the agent panel/palette
	// above, this ISN'T gated behind a click: the heavy engine (markdown
	// render, walkthrough spotlight math, AI-ask mode) still needs to stay
	// out of the entry chunk, so the dynamic import fires as soon as the
	// shell connects (see _mountHelp) — the button appears a beat after
	// first paint, once that async chunk resolves, rather than needing a
	// first interaction to exist at all.
	@state() private _helpMounted = false;

	// Stage 71 — command palette (Cmd+K). Lazy-mounted on first open so the
	// palette + matcher + adapters stay out of the entry bundle.
	@state() private _paletteMounted = false;
	@state() private _paletteOpen = false;
	@state() private _createPartnerOpen = false;
	@state() private _quickTaskOpen = false;
	// Stage 72 — keyboard-shortcut overlay (the `?` reference). Mount-on-open
	// so connect=open / disconnect=close drives focus + the modal lifecycle.
	@state() private _overlayMounted = false;
	// Stage 74 — the single app-wide side panel. _panelMounted lazy-loads
	// the primitive on first open + then stays mounted so the slide
	// transition plays; _panelState mirrors the SidePanelManager.
	@state() private _panelMounted = false;
	@state() private _panelState: PanelState = { open: false, activeTab: '' };
	private _unsubPanel: (() => void) | null = null;
	@state() private _overlayOpen = false;

	// Track the element that opened the overlay so focus can return to
	// it on close (a11y restore-focus pattern).
	private _opener: HTMLElement | null = null;

	static styles = css`
		:host {
			display: grid;
			/* Stage 96 — the expanded rail width is the per-user sidebar_width pref
			   (--cos-rail-width-active, set via data-sidebar-width on :root); compact /
			   absent falls back to the pre-96 260px. Collapsed stays 64px. */
			grid-template-columns: var(--cos-rail-width-active, var(--cos-rail-width-md)) 1fr;
			/* Workstream A1 — the header row grows by the top safe-area inset
			   (notch/status-bar height inside the Capacitor shell / a
			   home-screen PWA with no browser chrome above it) so the fixed
			   --cos-header-height content isn't pushed under it; see the
			   header rule below, which pads INTO that extra row height. 0px
			   on a normal browser tab, where env() resolves to nothing. */
			grid-template-rows: calc(var(--cos-header-height) + env(safe-area-inset-top, 0px)) 1fr;
			grid-template-areas:
				"sidebar header"
				"sidebar main";
			/* vh first: browsers without dvh support drop the dvh line and
			   keep this one (the cos-chat/cos-mail-inbox precedent). */
			min-height: 100vh;
			min-height: 100dvh;
			background: var(--cos-surface-raised);
		}

		header {
			grid-area: header;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			/* The right padding mirrors main's own docked-panel reflow (the
			   --cos-help-dock/--cos-feedback-dock rule below): a docked help
			   or feedback panel must push the Active chip / bell / avatar
			   left with the page, never sit over them (Paul, 2026-08-26). */
			padding: 0 calc(var(--cos-space-6) + var(--cos-help-dock, 0px) + var(--cos-feedback-dock, 0px)) 0 var(--cos-space-6);
			/* Workstream A1 — see the grid-template-rows comment above; this
			   is the padding that actually pushes header content below the
			   inset instead of just growing the row underneath it. */
			padding-top: env(safe-area-inset-top, 0px);
			box-sizing: border-box;
			background: var(--cos-surface);
			border-bottom: 1px solid var(--cos-border);
			box-shadow: var(--cos-shadow-sm);
			gap: var(--cos-space-3);
		}

		/* The trail (docs/design-system/breadcrumb-trail.md §3) takes the free
		   left half of the header; its auto right margin is what keeps the
		   status pill, feedback widget, bell and avatar right-aligned exactly
		   where they were. A zero min-width lets it shrink rather than push
		   them. */
		cos-trail {
			margin-right: auto;
			min-width: 0;
		}

		/* Hamburger button is hidden above 768px and visible below. */
		.hamburger {
			display: none;
			background: none;
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			width: 44px; height: 44px;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: var(--cos-text-primary);
			font-family: var(--cos-font-family);
			padding: 0;
			/* The order property puts it left of the trail at narrow widths
			   (§7: "it sits between the hamburger and the account avatar")
			   without changing the DOM order. The push-everything-else-right
			   job now belongs to cos-trail's own auto right margin — two auto
			   margins would split the free space and strand the trail in the
			   middle of the header. */
			order: -1;
			margin-right: var(--cos-space-2);
		}
		.hamburger:hover { background: var(--cos-surface-raised); }
		.hamburger:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
		.hamburger .bars {
			display: inline-block;
			width: 18px; height: 2px;
			background: currentColor;
			position: relative;
		}
		.hamburger .bars::before,
		.hamburger .bars::after {
			content: '';
			position: absolute;
			left: 0; width: 18px; height: 2px;
			background: currentColor;
		}
		.hamburger .bars::before { top: -6px; }
		.hamburger .bars::after  { top:  6px; }

		.user {
			display: flex;
			align-items: center;
			gap: var(--cos-space-3);
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
		}
		.user-meta {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			line-height: 1.2;
		}
		.user-name { color: var(--cos-text-primary); }
		.user-role {
			color: var(--cos-text-tertiary);
			font-size: var(--cos-font-size-xs);
		}
		/* Stage 66 — the transparency notice (brief posture: nothing
		   covert). One quiet line linking to the self-serve view. */
		.activity-note {
			color: var(--cos-text-tertiary);
			font-size: var(--cos-font-size-2xs);
			text-decoration: underline dotted;
		}
		.activity-note:hover { color: var(--cos-brand-700, var(--cos-brand-700)); }
		.user button {
			background: none;
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			padding: var(--cos-space-1) var(--cos-space-3);
			font-size: var(--cos-font-size-sm);
			cursor: pointer;
			color: var(--cos-text-secondary);
			font-family: var(--cos-font-family);
		}
		/* Stage 19f (audit issue 029): at <640px the role subtitle and
		   long "Sign out" label crowded the header. Hide the role on
		   mobile (it's redundant — internal tool, role is inferable);
		   compress "Sign out" to "Out" with the full label preserved
		   in aria-label for screen readers. */
		@media (max-width: 640px) {
			.user-role { display: none; }
			.user button .full { display: none; }
		}
		@media (min-width: 641px) {
			.user button .short { display: none; }
		}
		/* 65a-4 — one-click status pill + menu. */
		.status-wrap { position: relative; }
		.status-pill {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			background: none;
			border: 1px solid var(--cos-border);
			border-radius: 999px;
			padding: 4px 12px;
			font-size: var(--cos-font-size-sm);
			font-family: var(--cos-font-family);
			color: var(--cos-text-secondary);
			cursor: pointer;
			max-width: 180px;
		}
		.status-pill:hover { background: var(--cos-surface-raised); }
		.status-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.status-dot {
			width: 8px; height: 8px;
			border-radius: 50%;
			flex: none;
			background: var(--cos-status-success-text);
		}
		.status-dot.away { background: var(--cos-status-warning-dot); }
		.status-menu {
			position: absolute;
			top: calc(100% + 6px);
			right: 0;
			z-index: 50;
			background: var(--cos-surface);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			box-shadow: var(--cos-shadow-md, 0 4px 12px rgba(0,0,0,0.12));
			min-width: 170px;
			padding: 4px;
			display: flex;
			flex-direction: column;
		}
		.status-menu button {
			display: flex;
			align-items: center;
			gap: 8px;
			background: none;
			border: none;
			border-radius: 6px;
			padding: 7px 10px;
			font-size: var(--cos-font-size-sm);
			font-family: var(--cos-font-family);
			color: var(--cos-text-primary);
			cursor: pointer;
			text-align: left;
		}
		.status-menu button:hover { background: var(--cos-surface-raised); }
		.status-menu button.current { font-weight: 600; }
		@media (max-width: 640px) { .status-label { display: none; } .status-pill { padding: 4px 8px; } }
		/* Phone: header and page may shrink below their content's width, so
		   one wide control cannot push every page past the edge (the name and
		   status give way in the A-5 block further down). */
		@media (max-width: 640px) { header, main { min-width: 0; } }

		.user button:hover {
			background: var(--cos-surface-raised);
		}

		/* Stage 85a — user avatar dropdown (replaces the bare Sign out). */
		.user-wrap { position: relative; }
		.user-trigger {
			display: inline-flex;
			align-items: center;
			gap: var(--cos-space-2);
			background: none;
			border: 1px solid transparent;
			border-radius: var(--cos-radius-pill);
			padding: 4px 8px 4px 4px;
			cursor: pointer;
			font-family: var(--cos-font-family);
			color: var(--cos-text-secondary);
		}
		.user-trigger:hover { background: var(--cos-surface-raised); }
		.avatar {
			width: 28px; height: 28px;
			border-radius: var(--cos-radius-pill);
			background: var(--cos-avatar-bg);
			color: var(--cos-text-tertiary);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			font-size: var(--cos-font-size-xs);
			font-weight: var(--cos-font-weight-semibold);
			flex: none;
		}
		.user-menu {
			position: absolute;
			top: calc(100% + 6px);
			right: 0;
			z-index: var(--cos-z-dropdown, 20);
			background: var(--cos-surface);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			box-shadow: var(--cos-shadow-md);
			min-width: 190px;
			padding: 4px;
			display: flex;
			flex-direction: column;
		}
		.user-menu a,
		.user-menu button {
			display: flex;
			align-items: center;
			gap: var(--cos-space-2);
			background: none;
			border: none;
			border-radius: var(--cos-radius-sm);
			padding: 8px 10px;
			font-size: var(--cos-font-size-sm);
			font-family: var(--cos-font-family);
			color: var(--cos-text-primary);
			cursor: pointer;
			text-align: left;
			text-decoration: none;
		}
		.user-menu a:hover,
		.user-menu button:hover { background: var(--cos-surface-raised); }
		.user-menu .menu-note {
			color: var(--cos-text-tertiary);
			font-size: var(--cos-font-size-xs);
		}
		.user-menu .divider {
			height: 1px;
			background: var(--cos-border-soft);
			margin: 4px 0;
		}

		#primary-nav {
			grid-area: sidebar;
			background: var(--cos-charcoal);
			color: var(--cos-surface);
			/* Workstream A1 — the rail spans the FULL viewport height (both
			   grid rows), so it — unlike header, which sits entirely below
			   the inset by construction of the row-height bump above —
			   needs its own top/bottom/left safe-area padding directly: top
			   so the brand mark clears a notch/status bar, bottom so the
			   collapse toggle clears a home-indicator, left because it's
			   the leftmost column (matters in landscape on a device with a
			   rounded/notched side). All resolve to 0px on a normal browser
			   tab. */
			padding: calc(var(--cos-space-4) + env(safe-area-inset-top, 0px)) 0
				calc(var(--cos-space-4) + env(safe-area-inset-bottom, 0px))
				env(safe-area-inset-left, 0px);
			display: flex;
			flex-direction: column;
			/* Workstream A1 — a long nav (many entitled products + the
			   partner-nav customization groups) can exceed viewport height;
			   scroll instead of silently clipping the collapse toggle off
			   the bottom. overscroll-behavior:contain keeps an overscroll
			   at either end from bubbling into a pull-to-refresh gesture on
			   the page behind it. */
			overflow-y: auto;
			overscroll-behavior: contain;
			/* The rail scrolls INDEPENDENTLY of the page (Paul, 2026-08-06:
			   reaching the bottom of a long nav was scrolling the main view).
			   As a grid child spanning both rows it previously grew to the
			   full page height, so the overflow-y above never engaged — a
			   ~960px nav just made the document taller. Sticky + a viewport
			   height cap keeps the rail pinned while ITS OWN scrollbar
			   handles the overflow; the page scrollbar now reflects main
			   content only. box-sizing accounts for the safe-area padding.
			   The <768px drawer override below (position: fixed; inset)
			   already constrained itself, and its position/fixed wins over
			   sticky there. vh-then-dvh: the no-dvh fallback pattern. */
			position: sticky;
			top: 0;
			height: 100vh;
			height: 100dvh;
			box-sizing: border-box;
		}

		.brand {
			padding: var(--cos-space-1) var(--cos-space-6) var(--cos-space-4);
			border-bottom: 1px solid rgba(255, 255, 255, 0.08);
			margin-bottom: var(--cos-space-3);
			line-height: 0;
		}
		/* The Communico One logo (official artwork, white wordmark for the
		   dark rail). The narrow rail swaps in the teal "one" mark lifted
		   from the same artwork, since the full logo cannot fit in 64px. */
		.brand-logo { display: block; height: 22px; width: auto; }
		.brand-mark { display: none; height: 17px; width: auto; }

		#primary-nav a {
			display: flex;
			align-items: center;
			gap: var(--cos-space-3);
			color: rgba(255, 255, 255, 0.8);
			text-decoration: none;
			padding: var(--cos-space-3) var(--cos-space-6);
			font-size: var(--cos-font-size-sm);
			border-left: var(--cos-keyline-width) solid transparent;
		}
		#primary-nav a:hover {
			color: var(--cos-surface);
			background: rgba(255, 255, 255, 0.04);
		}
		#primary-nav a.active {
			color: var(--cos-surface);
			background: rgba(25, 180, 199, 0.12);
			border-left-color: var(--cos-brand-500);
		}
		#primary-nav a:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: -2px;
		}
		#primary-nav a .icon {
			color: var(--cos-brand-500);
			width: 20px;
			height: 20px;
			flex-shrink: 0;
		}
		#primary-nav a.active .icon {
			color: var(--cos-text-primary);
		}
		/* Plain-language nav: main label leads, module brand as a muted
		   subtitle underneath (Paul, 2026-07-17). The label is a flex
		   column; the collapsed rail hides the whole block (icon only). */
		#primary-nav a .nav-label {
			display: flex;
			flex-direction: column;
			line-height: 1.2;
			min-width: 0;
		}
		#primary-nav a .nav-sub {
			font-size: var(--cos-font-size-xs);
			color: rgba(255, 255, 255, 0.62);
			font-weight: 400;
		}
		#primary-nav a.active .nav-sub {
			color: rgba(255, 255, 255, 0.65);
		}
		/* Partner-nav customization: reorder + optional groups. Edit mode
		   is a per-user, always-quiet affordance on the charcoal rail.
		   Every plain-element button in this block (.nc-link/.nc-done/
		   .nc-group-del/.nc-row-hide/.nc-row-show) is hand-rolled, not
		   the design-system button primitive, DELIBERATELY: that
		   primitive's variants resolve theme tokens (text/surface colors
		   that flip with light/dark mode), while this rail is ALWAYS
		   charcoal — a themed button would go invisible in light mode.
		   Hand-rolled + rgba(255,255,255,…) is correct here
		   (element-baseline.txt is widened accordingly). */
		.nav-custom-bar {
			display: flex;
			align-items: center;
			padding: var(--cos-space-2) var(--cos-space-6) var(--cos-space-1);
		}
		.nav-custom-bar .nc-spacer { flex: 1; }
		.nav-custom-bar .nc-link,
		.nav-custom-bar .nc-done {
			display: inline-flex;
			align-items: center;
			gap: 4px;
			background: none;
			border: none;
			cursor: pointer;
			color: rgba(255, 255, 255, 0.55);
			font-size: var(--cos-font-size-xs);
			padding: 2px 4px;
			border-radius: var(--cos-radius-sm);
		}
		.nav-custom-bar .nc-link:hover,
		.nav-custom-bar .nc-done:hover {
			color: var(--cos-surface);
			background: rgba(255, 255, 255, 0.06);
		}
		.nav-custom-bar.editing .nc-done { color: var(--cos-brand-500); font-weight: 500; }
		:host([collapsed]) .nav-custom-bar { display: none; }
		.group-label {
			font-size: var(--cos-font-size-xs);
			text-transform: uppercase;
			letter-spacing: 0.06em;
			color: rgba(255, 255, 255, 0.4);
			font-weight: 500;
			padding: var(--cos-space-3) var(--cos-space-6) var(--cos-space-1);
		}
		:host([collapsed]) .group-label { display: none; }
		.nc-group-head {
			display: flex;
			align-items: center;
			gap: 4px;
			padding: var(--cos-space-2) var(--cos-space-5) var(--cos-space-1);
		}
		.nc-group-name {
			flex: 1;
			min-width: 0;
			background: rgba(255, 255, 255, 0.08);
			border: 1px solid rgba(255, 255, 255, 0.14);
			border-radius: var(--cos-radius-sm);
			color: var(--cos-surface);
			font-size: var(--cos-font-size-xs);
			text-transform: uppercase;
			letter-spacing: 0.06em;
			padding: 3px 6px;
		}
		.nc-group-name:focus-visible { outline: 2px solid var(--cos-brand-500); outline-offset: 1px; }
		.nc-group-del {
			background: none;
			border: none;
			cursor: pointer;
			color: rgba(255, 255, 255, 0.4);
			padding: 2px;
			border-radius: var(--cos-radius-sm);
			display: inline-flex;
		}
		.nc-group-del:hover { color: var(--cos-status-danger-text); background: rgba(255, 255, 255, 0.06); }
		.nav-edit-row {
			position: relative;
			display: flex;
			align-items: center;
			gap: var(--cos-space-3);
			padding: var(--cos-space-3) var(--cos-space-6);
			color: rgba(255, 255, 255, 0.8);
			cursor: grab;
			border-left: var(--cos-keyline-width) solid transparent;
		}
		.nav-edit-row:hover { background: rgba(255, 255, 255, 0.04); }
		.nav-edit-row.dragging { opacity: 0.4; }
		/* Insertion line — a bright brand rule at the exact drop point. */
		.nav-edit-row.drop-before::before,
		.nav-edit-row.drop-after::after {
			content: '';
			position: absolute;
			left: var(--cos-space-5);
			right: var(--cos-space-4);
			height: 2px;
			background: var(--cos-brand-500);
			border-radius: 1px;
			pointer-events: none;
		}
		.nav-edit-row.drop-before::before { top: -1px; }
		.nav-edit-row.drop-after::after { bottom: -1px; }
		/* Target-section highlight (append / empty group). */
		.nav-section.dropping { background: rgba(25, 180, 199, 0.1); }
		.nav-section.dropping .nc-empty { border-color: var(--cos-brand-500); color: var(--cos-surface); }
		.nav-edit-row .nc-grip { color: rgba(255, 255, 255, 0.35); flex-shrink: 0; }
		.nav-edit-row .icon { color: var(--cos-brand-500); width: 20px; height: 20px; flex-shrink: 0; }
		.nav-edit-row .nav-label {
			flex: 1;
			display: flex;
			flex-direction: column;
			line-height: 1.2;
			min-width: 0;
			font-size: var(--cos-font-size-sm);
		}
		.nav-edit-row .nav-sub { font-size: var(--cos-font-size-xs); color: rgba(255, 255, 255, 0.5); font-weight: 400; }
		.nc-row-hide,
		.nc-row-show {
			background: none;
			border: none;
			cursor: pointer;
			color: rgba(255, 255, 255, 0.35);
			padding: 2px 4px;
			border-radius: var(--cos-radius-sm);
			display: inline-flex;
			align-items: center;
			gap: 4px;
			flex-shrink: 0;
		}
		.nc-row-hide:hover { color: var(--cos-surface); background: rgba(255, 255, 255, 0.08); }
		.nc-hidden-list {
			border-top: 1px solid rgba(255, 255, 255, 0.08);
			margin-top: var(--cos-space-2);
			padding-top: var(--cos-space-1);
		}
		.nc-hidden-row {
			cursor: default;
			color: rgba(255, 255, 255, 0.45);
		}
		.nc-hidden-row .icon { color: rgba(255, 255, 255, 0.35); }
		.nc-row-show { color: var(--cos-brand-500); font-size: var(--cos-font-size-xs); }
		.nc-row-show:hover { background: rgba(255, 255, 255, 0.08); }
		.nc-empty {
			margin: 2px var(--cos-space-6);
			padding: var(--cos-space-3);
			border: 1px dashed rgba(255, 255, 255, 0.2);
			border-radius: var(--cos-radius-sm);
			color: rgba(255, 255, 255, 0.4);
			font-size: var(--cos-font-size-xs);
			text-align: center;
		}

		/* Collapse toggle — styled like a nav row, pinned above the foot. */
		.collapse-toggle {
			display: flex;
			align-items: center;
			gap: var(--cos-space-3);
			width: 100%;
			background: none;
			border: none;
			border-top: 1px solid rgba(255, 255, 255, 0.08);
			color: rgba(255, 255, 255, 0.6);
			cursor: pointer;
			padding: var(--cos-space-3) var(--cos-space-6);
			font-family: var(--cos-font-family);
			font-size: var(--cos-font-size-sm);
			text-align: left;
		}
		.collapse-toggle:hover { color: var(--cos-surface); background: rgba(255, 255, 255, 0.04); }
		.collapse-toggle .icon { color: var(--cos-brand-500); width: 18px; height: 18px; flex-shrink: 0; }
		.collapse-toggle:focus-visible { outline: 2px solid var(--cos-brand-500); outline-offset: -2px; }

		/* ---- Collapsed icon-rail state (desktop only) ---- */
		:host([collapsed]) {
			grid-template-columns: 64px 1fr;
		}
		:host([collapsed]) #primary-nav a,
		:host([collapsed]) .collapse-toggle {
			justify-content: center;
			gap: 0;
			padding-left: 0;
			padding-right: 0;
		}
		:host([collapsed]) #primary-nav .nav-label { display: none; }
		:host([collapsed]) .brand { padding: var(--cos-space-1) 0 var(--cos-space-4); text-align: center; }
		:host([collapsed]) .brand-logo { display: none; }
		:host([collapsed]) .brand-mark { display: inline-block; }
		/* Keep the chat badge visible but tuck it onto the icon corner. */
		:host([collapsed]) #primary-nav a .nav-badge {
			position: absolute;
			margin-left: 0;
			transform: translate(10px, -10px);
			padding: 0 5px;
		}
		:host([collapsed]) #primary-nav a { position: relative; }
		/* Collapsed rail is icon-only; the gate marker would crowd the
		   single nav icon with no room for its own tooltip — the row's
		   existing data-label tooltip already names the surface. */
		:host([collapsed]) #primary-nav a .nav-gate-marker { display: none; }
		/* The rail's tooltips overflow into the main column; without a
		   stacking context the (later-painted) main content would cover
		   them. Desktop-only — the mobile overlay is position:fixed. */
		/* Keep the rail's independent-scroll stickiness in collapsed mode —
		   sticky creates the same stacking context this z-index needs (the
		   old position: relative would have silently reverted the rail to
		   growing with the page). */
		:host([collapsed]) #primary-nav { position: sticky; z-index: 30; }

		/* ---- Workspace switcher (Communico | Partner) ---- */
		.ws-switch {
			display: flex;
			gap: 2px;
			margin: 0 var(--cos-space-4) var(--cos-space-3);
			padding: 3px;
			background: rgba(255, 255, 255, 0.06);
			border-radius: var(--cos-radius-pill);
		}
		.ws-switch button {
			flex: 1;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 6px;
			font: inherit;
			font-size: var(--cos-font-size-xs);
			font-weight: 600;
			color: rgba(255, 255, 255, 0.7);
			background: none;
			border: none;
			border-radius: var(--cos-radius-pill);
			padding: 5px 8px;
			cursor: pointer;
			white-space: nowrap;
			position: relative;
		}
		.ws-switch button:hover { color: var(--cos-surface); }
		.ws-switch button.on {
			background: var(--cos-brand-700);
			color: var(--cos-text-on-color);
		}
		.ws-switch button:focus-visible { outline: 2px solid var(--cos-brand-500); outline-offset: 1px; }
		.ws-switch .icon { width: 16px; height: 16px; flex-shrink: 0; }
		:host([collapsed]) .ws-switch {
			flex-direction: column;
			margin: 0 var(--cos-space-2) var(--cos-space-3);
		}
		:host([collapsed]) .ws-switch .nav-label { display: none; }

		/* ---- Shared legacy-client picker (Partner workspace top) ----
		   One client selection covering every control-panel-ported
		   module; module pages read it via services/legacy-client. */
		.client-pick {
			display: flex;
			flex-direction: column;
			gap: 4px;
			margin: 0 var(--cos-space-3) var(--cos-space-3);
		}
		.client-pick label {
			font-size: var(--cos-font-size-xs);
			font-weight: 600;
			color: rgba(255, 255, 255, 0.55);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}
		.client-pick input {
			font: inherit;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-surface);
			background: rgba(255, 255, 255, 0.06);
			border: 1px solid rgba(255, 255, 255, 0.15);
			border-radius: var(--cos-radius-md);
			padding: 6px 8px;
			width: 100%;
			box-sizing: border-box;
		}
		.client-pick input::placeholder { color: rgba(255, 255, 255, 0.45); }
		.client-pick input:focus-visible { outline: 2px solid var(--cos-brand-500); outline-offset: 1px; }
		/* Typeahead over every enabled client — a plain select is unusable
		   at 300+ options, which is what staff see. */
		.cp-wrap { position: relative; }
		/* "You are viewing this as X" — a full-screen scrim so the next
		   click anywhere dismisses it, whatever it lands on. */
		.viewing-as {
			position: fixed;
			inset: 0;
			z-index: var(--cos-z-modal, 1000);
			display: flex;
			align-items: center;
			justify-content: center;
			background: color-mix(in srgb, black 40%, transparent);
		}
		.viewing-as-card {
			background: var(--cos-surface);
			color: var(--cos-text-primary);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-card);
			box-shadow: 0 4px 16px var(--cos-shadow-lg);
			padding: var(--cos-space-6) var(--cos-space-8);
			max-width: 420px;
			text-align: center;
		}
		.viewing-as-card strong {
			display: block;
			font-family: var(--cos-font-display);
			font-weight: 400;
			font-size: var(--cos-font-size-2xl);
			margin: var(--cos-space-2) 0;
		}
		.viewing-as-card .hint {
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-tertiary);
		}
		/* The prompt that stands in for a partner surface until a library
		   is chosen. */
		.pick-first {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 60vh;
			padding: var(--cos-space-8);
		}
		.cp-list {
			position: absolute;
			left: 0;
			right: 0;
			top: calc(100% + 4px);
			z-index: 40;
			max-height: 280px;
			overflow-y: auto;
			padding: 4px;
			background: var(--cos-surface);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-md);
			box-shadow: 0 6px 16px var(--cos-shadow-lg);
		}
		.cp-opt {
			display: flex;
			align-items: baseline;
			justify-content: space-between;
			gap: var(--cos-space-2);
			padding: 6px 8px;
			border-radius: var(--cos-radius-sm);
			color: var(--cos-text-primary);
			font-size: var(--cos-font-size-sm);
			cursor: pointer;
		}
		.cp-opt.on,
		.cp-opt:hover { background: var(--cos-surface-raised); }
		.cp-opt[aria-selected="true"] .cp-name { font-weight: 600; }
		.cp-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.cp-mods {
			flex: none;
			font-size: var(--cos-font-size-2xs);
			color: var(--cos-text-tertiary);
			white-space: nowrap;
		}
		.cp-note {
			padding: 6px 8px;
			font-size: var(--cos-font-size-xs);
			color: var(--cos-text-tertiary);
		}
		:host([collapsed]) .client-pick { display: none; }

		/* ---- Collapsed-rail hover tooltips ----
		   The icon rail needs to say what each icon IS. A styled tooltip
		   (immediate, not the slow native title) rides data-label on every
		   nav link + switcher tab, shown only while collapsed. */
		:host([collapsed]) #primary-nav a[data-label]::after,
		:host([collapsed]) .ws-switch button::after {
			content: attr(data-label);
			position: absolute;
			left: calc(100% + 10px);
			top: 50%;
			transform: translateY(-50%);
			background: var(--cos-charcoal);
			color: var(--cos-surface);
			border: 1px solid rgba(255, 255, 255, 0.15);
			border-radius: var(--cos-radius-sm);
			padding: 4px 10px;
			font-size: var(--cos-font-size-xs);
			font-weight: 500;
			white-space: nowrap;
			box-shadow: 0 2px 8px var(--cos-shadow-lg);
			opacity: 0;
			visibility: hidden;
			pointer-events: none;
			transition: opacity 120ms ease;
			z-index: 40;
		}
		:host([collapsed]) #primary-nav a[data-label]:hover::after,
		:host([collapsed]) #primary-nav a[data-label]:focus-visible::after,
		:host([collapsed]) .ws-switch button:hover::after,
		:host([collapsed]) .ws-switch button:focus-visible::after {
			opacity: 1;
			visibility: visible;
		}
		/* Admin two-column layout: grouped settings nav + the page. */
		.admin-layout {
			display: grid;
			grid-template-columns: 220px minmax(0, 1fr);
			gap: var(--cos-space-6);
			align-items: start;
		}
		.admin-body { min-width: 0; }
		@media (max-width: 900px) {
			.admin-layout { grid-template-columns: 1fr; gap: var(--cos-space-4); }
		}
		main {
			grid-area: main;
			/* overflow-y: auto removed (Stage 46-polish). It was an inert
			   scrollport — the document is the real scroller — and it
			   trapped scrollIntoView app-wide (see stage-46f.md). */
			/* --cos-main-pad is PUBLISHED for slotted content: the record
			   shell's full-bleed negative margin consumes it, so the bleed
			   always exactly negates whatever main actually pads (the two
			   drifted at compact — 16px pad vs a hard-coded -24px bleed =
			   8px/side page overflow on phones). */
			--cos-main-pad: var(--cos-space-6);
			padding: var(--cos-main-pad);
			/* A docked side panel (e.g. the Circulate help panel, or the
			   feedback panel — --cos-feedback-dock) sets its own custom
			   property on documentElement; main gives up that much width
			   so the panel sits BESIDE the page, never over it. Each panel
			   owns a distinct property (they can coexist, e.g. on
			   /circulate) so one panel's cleanup can't clobber the other's.
			   Unset (the normal case) resolves to 0 — no layout change. */
			padding-right: calc(var(--cos-space-6) + var(--cos-help-dock, 0px) + var(--cos-feedback-dock, 0px));
			/* Marcie R7 (1c / 1e / the "funky gray band") — THE root fix.
			   main is a GRID ITEM, and a grid item's default min-width:auto
			   refuses to shrink below its content's min-content width. So the
			   wide ticket table (1960px of column widths) stretched this column
			   — and with it the whole document — to 2372px at a 1280px viewport.
			   That one overflow produced all three symptoms: 1092px of
			   left-right page scrolling (1e), content "sliced off" when not
			   full screen (1c), and the grey band (the body colour showing
			   beyond the viewport-sized header/KPI containers).
			   min-width:0 lets the column stay 1fr; the overflow then lands
			   where it belongs — inside cos-data-table's own .scroll container,
			   which already has overflow-x:auto and a clipping host. */
			min-width: 0;
		}

		.spacer { flex: 1; }
		/* Responsive Workstream B — the gated-item nav marker. Sits before
		   the unread badge (a row is never both, but order is defensive);
		   opacity keeps it quiet next to the label, never competing with
		   it — discoverability, not a warning. */
		.nav-gate-marker {
			margin-left: auto;
			color: var(--cos-text-tertiary);
			opacity: 0.7;
			flex-shrink: 0;
		}
		.nav-badge {
			margin-left: auto;
			background: var(--cos-brand-500, var(--cos-brand-500)); color: var(--cos-surface);
			border-radius: 999px; font-size: var(--cos-font-size-2xs); font-weight: 700;
			padding: 1px 7px; line-height: 1.4;
		}
		.foot {
			padding: var(--cos-space-3) var(--cos-space-6);
			color: rgba(255, 255, 255, 0.7);
			font-size: var(--cos-font-size-xs);
			border-top: 1px solid rgba(255, 255, 255, 0.08);
		}

		.backdrop { display: none; }

		@media (max-width: 768px) {
			:host {
				/* minmax(0, ...) so a wide child (a table, the header's own
				   controls) can never stretch the page past the phone screen
				   (Nurture QA C-16). */
				grid-template-columns: minmax(0, 1fr);
				grid-template-areas:
					"header"
					"main";
			}
			.hamburger { display: inline-flex; }
			header { padding: 0 var(--cos-space-4); }
			main { --cos-main-pad: var(--cos-space-4); padding: var(--cos-main-pad); }
			/* Sidebar transitions in/out as a fixed-position drawer.
			   When closed it's translated off-screen; when open it
			   slides in over the main content with a backdrop. */
			#primary-nav {
				position: fixed;
				inset: 0 auto 0 0;
				width: min(280px, 80vw);
				transform: translateX(-100%);
				transition: transform 240ms var(--cos-ease-drawer);
				z-index: 100;
			}
			#primary-nav.open { transform: translateX(0); }
			/* Responsive Workstream A2 — the drawer's own touch targets. The
			   desktop rail's ~42px nav-row height (padding-3 + sm text) is left
			   alone (it's a mouse surface); the OVERLAY DRAWER is a touch
			   surface, so its rows get a min-height floor instead of larger
			   font/padding tokens ("padding, not font inflation" per spec §6). */
			#primary-nav a, .collapse-toggle, .ws-switch button {
				min-height: 44px;
				box-sizing: border-box;
			}
			/* Collapse is a desktop-only affordance — the mobile drawer
			   always shows full labels and hides the toggle. */
			:host([collapsed]) { grid-template-columns: 1fr; }
			:host([collapsed]) #primary-nav .nav-label { display: inline; }
			:host([collapsed]) #primary-nav a,
			:host([collapsed]) .collapse-toggle {
				justify-content: flex-start;
				gap: var(--cos-space-3);
				padding-left: var(--cos-space-6);
				padding-right: var(--cos-space-6);
			}
			:host([collapsed]) .brand { padding: var(--cos-space-1) var(--cos-space-6) var(--cos-space-4); text-align: left; }
			:host([collapsed]) .brand-logo { display: block; }
			:host([collapsed]) .brand-mark { display: none; }
			.collapse-toggle { display: none; }
			.backdrop {
				display: block;
				position: fixed;
				inset: 0;
				background: rgba(0, 0, 0, 0.5);
				opacity: 0;
				pointer-events: none;
				transition: opacity 240ms var(--cos-ease-out);
				z-index: 99;
			}
			.backdrop.open {
				opacity: 1;
				pointer-events: auto;
			}
		}

		@media (prefers-reduced-motion: reduce) {
			#primary-nav { transition: none; }
		}

		/* A phone-width header (Nurture QA A-5). Everything stays reachable:
		   the status pill shrinks to its dot (the menu it opens still names
		   each status, and its title and label carry the current one), the
		   person's name and role move into the top of the account menu, and
		   the gaps tighten so the page trail keeps a readable share. Last in
		   the sheet so these win over the unconditional rules above. */
		.menu-who { display: none; }
		@media (max-width: 640px) {
			header { gap: var(--cos-space-2); }
			.status-pill { padding: 4px 8px; }
			.status-pill .status-label { display: none; }
			.user { gap: var(--cos-space-1); }
			.user-trigger .user-meta { display: none; }
			.menu-who {
				display: flex;
				flex-direction: column;
				padding: var(--cos-space-2) var(--cos-space-3);
				border-bottom: 1px solid var(--cos-border-soft);
				margin-bottom: var(--cos-space-1);
			}
			.menu-who .user-role { display: block; }
		}

		/* Stage 30 — admin agent FAB. Bottom-right of the viewport,
		 * above any page content. Admin-only (gated in render()).
		 * z-index sits above the panel itself so the FAB stays
		 * pressable when the panel is closed; the panel's own z-index
		 * is higher than other surfaces but lower than this for the
		 * "FAB always reachable" UX. That same z-index also means an
		 * OPEN side panel's own bottom-right content (e.g. a form's
		 * Save button) can land right under the FAB — feedback 187.
		 * _agentFabStyle() shifts the right offset clear of the open
		 * panel's width so the FAB stays reachable without sitting on
		 * top of whatever the panel is showing there. */
		.agent-fab {
			position: fixed;
			right: 24px;
			bottom: 24px;
			width: 48px;
			height: 48px;
			border-radius: 50%;
			background: var(--cos-brand-700);
			color: var(--cos-surface);
			border: none;
			cursor: pointer;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 95;
			transition: background 150ms ease, transform 150ms ease;
		}
		.agent-fab:hover {
			background: var(--cos-brand-800);
		}
		@media (hover: hover) and (pointer: fine) {
			.agent-fab:hover {
				transform: translateY(-1px);
			}
		}
		.agent-fab:active {
			transform: scale(0.97);
		}
		.agent-fab:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 3px;
		}
		@media (prefers-reduced-motion: reduce) {
			.agent-fab { transition: none; }
			.agent-fab:hover,
			.agent-fab:active { transform: none; }
		}

		/* Responsive Workstream C — connection banner (top, persistent
		   while offline) + SW-update toast (bottom-right, one-shot). Two
		   deliberately different signals: the banner says "you can't do
		   anything right now"; the toast says "something's ready when you
		   are". See services/connection.ts + services/serviceWorker.ts. */
		.conn-banner {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			z-index: var(--cos-z-toast);
			display: flex;
			align-items: center;
			justify-content: center;
			gap: var(--cos-space-2);
			padding: var(--cos-space-2) var(--cos-space-4);
			background: var(--cos-status-danger-wash);
			color: var(--cos-status-danger-text);
			font-size: var(--cos-font-size-sm);
			font-weight: var(--cos-font-weight-medium);
			text-align: center;
		}
	`;

	private _notifsWS: NotificationsWS | null = null;
	private _unregisterGlobals: (() => void) | null = null;
	// Responsive Workstream C — connectivity + SW-update UI state.
	@state() private _offline = !connectionMonitor.isOnline();
	private _unsubConnection: (() => void) | null = null;

	connectedCallback() {
		super.connectedCallback();
		// The trail's leaf: whatever the current record page last published.
		// subscribeLeaf fires immediately with the current value, so a shell
		// remount mid-record keeps the name it already had.
		this._unsubscribeLeaf = subscribeLeaf(() => { this._trailRevision++; });
		this._trailSearch = window.location.search;
		// A same-path query navigation never changes `path`, so the trail
		// would otherwise keep showing the previous sub-application view.
		window.addEventListener('cos:querychange', this._onTrailQueryChange);
		window.addEventListener('popstate', this._onTrailQueryChange);
		// Staff feedback platform — wraps console.error/warn + window.fetch so
		// a filed report automatically carries recent console/network errors.
		// Idempotent; safe across shell remounts.
		installFeedbackContextCapture();
		// Restore the persisted sidebar-collapsed preference (per device).
		try {
			this.collapsed = localStorage.getItem('cos-nav-collapsed') === '1';
		} catch { /* private mode / disabled storage — default expanded */ }
		document.addEventListener('keydown', this._onDocKeydown);
		// Stage 72 — register the global shortcut set (Cmd+K palette, ?
		// overlay, Esc, g-chord nav, admin Cmd+/ agent). The single
		// document keydown listener above dispatches through the registry.
		this._unregisterGlobals = registerGlobalShortcuts({
			openPalette: () => void this._openPalette(),
			openShortcutOverlay: () => void this._openOverlay(),
			toggleAgentPanel: () => void this._toggleAgent(),
			closeOverlays: () => this._closeOverlays(),
			isAdmin: () => this._isAdmin(),
			isStaff: () => this.userLevel === 1,
		});
		// Stage 74 — mirror the side-panel manager into a local @state so the
		// shell re-renders the one panel instance on open/close/tab change.
		this._unsubPanel = sidePanelManager.subscribe(() => this._onPanelChange());
		// Impersonation (docs/impersonation-plan.md) — staff-only capability
		// + whatever session (if any) this tab already holds. Read the
		// locally-held session synchronously first (so a mid-session page
		// reload shows the banner immediately, with no flash), then listen
		// for changes from wherever they happen (the dialog, the banner's
		// own Stop/Allow-changes actions, or a fresh-tab reconciliation).
		if (this.userLevel === 1) {
			void canImpersonate().then((on) => { this._canImpersonate = on; });
		}
		this._impersonation = currentImpersonationSession();
		window.addEventListener(IMPERSONATION_EVENT, this._onImpersonationChanged as EventListener);
		this._pinWorkspaceForImpersonation();
		// Community module enablement — loaded for EVERY user level (it is a
		// deployment-level flag, and the level-3 partner nav surfaces it too,
		// unlike the staff-only circulate products below).
		void communityEnabled().then((on) => { this._communityEnabled = on; });
		// Circulate/Lend product entitlements for the nav filter (staff
		// only — partner nav returns before the filter runs).
		if (this.userLevel >= 3) {
			// A library login's rail shows the products its library owns
			// (LIBRARY_RAIL_ALLOW narrows them). /circulate/context is
			// open to every authenticated caller and needs no client picker.
			void circulateProducts().then((p) => {
				this._circProducts = p;
			});
		}
		if (this.userLevel < 3) {
			// The library picker now backs every partner-side surface
			// (legacyClientId() has 84 call sites across 51 files, not just
			// the four control-panel-ported modules), so it must load
			// unconditionally and eagerly, never gated behind whichever
			// entitlements circulateProducts() resolves to. That left it
			// never loading for e.g. a Desk-only staffer, and made the
			// library-choice gate's "awaiting" decision unknowable until
			// that promise settled (see navigation/library-gate.ts).
			void this._loadLegacyClients();
			void circulateProducts().then((p) => {
				this._circProducts = p;
			});
			// Per-user Partner-nav layout (reorder + optional groups). One
			// GET of the prefs blob; a bad/absent value → default order.
			void api
				.getMyPreferences()
				.then((prefs) => {
					const raw = (prefs as Record<string, unknown> | null)?.nav_layout as NavLayoutDoc | undefined;
					this._navLayout = raw && Array.isArray(raw.sections) ? raw : null;
				})
				.catch(() => {
					/* absent / offline → default order */
				});
		}
		window.addEventListener(LEGACY_CLIENT_EVENT, this._onLegacyClientChanged as EventListener);
		// 65a-3b — chat unread badge plumbing.
		window.addEventListener('cos:chat-unread-sync', this._onChatUnreadSync as EventListener);
		window.addEventListener('cos:chat-message', this._onChatFrame as EventListener);
		window.addEventListener('cos:chat-status', this._onChatStatus as EventListener);
		// Action Center nav badge — refresh immediately on any action taken
		// on the page itself, and poll every 60s otherwise (see the _acUnread
		// doc comment for the visibility gate `_refreshActionCenterCount` applies).
		window.addEventListener('cos:action-center-changed', this._onActionCenterChanged);
		void this._refreshActionCenterCount();
		this._acPollTimer = setInterval(() => void this._refreshActionCenterCount(), 60_000);
		// Stage 71 — palette "Create partner" action (literal string =
		// OPEN_CREATE_PARTNER_EVENT; not imported, to keep commands/* out of
		// the entry bundle).
		window.addEventListener('cos:open-create-partner', this._onOpenCreatePartner);
		window.addEventListener('cos:open-quick-task', this._onOpenQuickTask);
		// fleet/desk-board: a page's own `/` affordance (Desk's branch bar
		// and rail footer) opens the SAME global palette instance rather than
		// building a second one; mirrors cos:open-create-partner/quick-task's
		// own "page dispatches, shell owns the overlay" shape exactly.
		window.addEventListener('cos:open-command-palette', this._onOpenCommandPalette as EventListener);
		window.addEventListener('cos:request-nav-space', this._onNavSpaceRequest as EventListener);
		window.addEventListener(OPEN_CLIENT_PICKER_EVENT, this._onOpenClientPicker);
		// The Cmd+Shift+F global shortcut dispatches this (feedback widget
		// lives in the header, not the keyboard module) rather than calling
		// the shell directly.
		window.addEventListener('cos:feedback:open', this._onFeedbackOpen);
		// Feedback #10 — the bell/notifications-page feedback.* rows dispatch
		// this (literal string = OPEN_FEEDBACK_REPORT_EVENT,
		// commands/feedbackReport.ts; not imported, to keep that module out
		// of this eager entry-bundle file — the cos:open-create-partner
		// precedent above) when there's no in-app page to link to. Reuses
		// the exact "open focused on a report" leg the re-test email link
		// already built (_openFeedbackReportFromRetestLink below).
		window.addEventListener('cos:feedback:open-report', this._onFeedbackOpenReport as EventListener);
		// Stage 85a — close the user dropdown on an outside click.
		window.addEventListener('click', this._onWindowClickUser, true);
		void this._refreshChatUnread();
		// Viewport-breakpoint tracking (drives `inert` on the closed nav so it
		// switches off the moment the viewport widens past compact — a tablet
		// rotated to landscape while the menu was open must not leave the
		// inert sidebar blocking the persistent desktop layout) now lives in
		// `_viewport` (ViewportController, added via `addController` at field-
		// init time) — its own hostConnected(), invoked by super.connectedCallback()
		// above, sets up the matchMedia listeners. Nothing to do here.
		// Stage 38a — open the notification WebSocket. The URL comes from
		// apiWebSocketURL so the socket tracks API_BASE: it was built from
		// window.location.host, which is right only while the API is
		// same-origin, and would have kept pointing at the app's own host on
		// the day the base moves to api.communico.co. The auth token comes
		// from api.getToken().
		if (typeof window !== 'undefined' && 'WebSocket' in window) {
			this._notifsWS = new NotificationsWS(apiWebSocketURL('/ws'), () => api.getToken());
			this._notifsWS.connect();
		}

		// Responsive Workstream C — the connection banner + SW-update toast.
		this._offline = !connectionMonitor.isOnline();
		this._unsubConnection = connectionMonitor.subscribe((online) => {
			this._offline = !online;
		});

		// In-app help — fire the dynamic import immediately (not gated
		// behind a click; see _helpMounted's doc comment above).
		void this._mountHelp();
		// Circulate's internal workspace-tab state lives on `?m=` in the
		// URL (cos-circulate-page.ts), not as a property this shell can
		// bind to — see cos-circulate-help.ts's doc comment. Both signals
		// just trigger a re-render; the value itself is read straight off
		// the URL at render time (_circulateActiveModule), never cached.
		window.addEventListener('cos:querychange', this._onHelpRouteSignal);
		window.addEventListener('popstate', this._onHelpRouteSignal);
	}

	private _onLegacyClientChanged = (e: CustomEvent<{ id: number }>) => {
		this._legacyClientId = e.detail?.id ?? 0;
	};

	private _onImpersonationChanged = (e: CustomEvent<{ session: ImpersonationSession | null }>) => {
		this._impersonation = e.detail?.session ?? null;
		if (this._impersonation) this._workspace = 'partner';
	};

	/**
	 * A live session IS the Partner workspace, so pin it there on boot.
	 *
	 * Without this the confinement rule eats itself: `_workspace` starts
	 * at 'communico', and the route-sync that would move it is gated on
	 * `userLevel < 3` while the effective level under impersonation is the
	 * TARGET's — so every page load looked like "navigated away" and ended
	 * the session before the first paint.
	 */
	private _pinWorkspaceForImpersonation(): void {
		if (currentImpersonationSession()) this._workspace = 'partner';
	}

	private _openImpersonateDialog(): void {
		if (this._awaitingClient()) return; // no library chosen — the menu item is disabled for this case too
		this._userMenuOpen = false;
		this._impersonateDialogOpen = true;
	}

	private async _stopImpersonating(): Promise<void> {
		if (this._stoppingImpersonation) return;
		this._stoppingImpersonation = true;
		this._userMenuOpen = false;
		try {
			await stopImpersonation();
		} finally {
			// Reload so the whole page re-fetches under the real identity
			// rather than leaving stale, target-scoped data on screen —
			// same reasoning as cos-impersonation-banner's own Stop button.
			window.location.reload();
		}
	}

	/** The library list could not be loaded. Only matters while the gate is
	 *  up: the prompt then offers a retry instead of a button that would
	 *  open a picker which is not rendered (it needs a non-empty list). */
	@state() private _legacyClientsFailed = false;

	private async _loadLegacyClients(force = false): Promise<void> {
		try {
			this._legacyClients = await loadLegacyClients(force);
			this._legacyClientsFailed = false;
			this._legacyClientId = legacyClientId();
			// Single-client staff never need to choose. (Mirrors
			// ensureLegacyClientAutoPicked in services/legacy-client.ts,
			// which app.ts's route guard uses for the same auto-pick: kept
			// as its own call here, rather than delegating to it, so this
			// method still goes through loadLegacyClients() directly and a
			// test can mock that one call in isolation.)
			if (this._legacyClientId === 0 && this._legacyClients.length === 1) {
				pickLegacyClient(this._legacyClients[0].id);
			}
		} catch {
			// The gate now stands in front of every partner surface, so a
			// failed load must not strand staff on a prompt whose only
			// button opens a picker that never rendered. Recorded here and
			// answered with a retry in the prompt itself.
			this._legacyClientsFailed = true;
			// A library already chosen for this tab still holds: offline Desk picks the library it
			// last ran for before the shell is listening, and the list cannot load to confirm it.
			this._legacyClientId = legacyClientId();
		}
	}

	// A page (the Measure drill's "choose a library" gate, the whole-page
	// .pick-first prompt below) asked the shell to open its picker —
	// dispatched as a window event since the asking page lives outside this
	// component's shadow root (the cos:open-create-partner precedent).
	private _onOpenClientPicker = (): void => {
		this._focusClientPicker();
	};

	/**
	 * Makes the library picker visible and puts the caret in it, wherever
	 * the sidebar currently is: switches to the Partner workspace if
	 * somehow not there already, expands the icon-only rail if collapsed,
	 * opens the mobile nav overlay on a narrow viewport, then opens the
	 * dropdown and focuses + selects the input so typing replaces whatever
	 * is already chosen.
	 */
	private _focusClientPicker(): void {
		if (this._workspace !== 'partner') this._workspace = 'partner';
		if (this.collapsed) this.collapsed = false;
		if (this._isNarrow && !this._navOpen) this._navOpen = true;
		this._clientOpen = true;
		this._clientQuery = null;
		this._clientActive = Math.max(
			this._legacyClients.findIndex((c) => c.id === this._legacyClientId),
			0,
		);
		void this.updateComplete.then(() => {
			const input = this.renderRoot.querySelector<HTMLInputElement>('#nav-client');
			input?.focus();
			input?.select();
		});
	}

	/* ---- Client picker (searchable) -------------------------------------
	   Communico staff support every library, so the list is every enabled
	   client — a few hundred rows. Type to narrow; arrows + Enter pick. */

	private _selectedClientName(): string {
		const listed = this._legacyClients.find((c) => c.id === this._legacyClientId)?.name;
		if (listed) return listed;
		// The chosen library is not in the picker's list (or the list has not
		// loaded): fall back to the name read from the library's own record,
		// so the box never sits empty on a library that is plainly chosen.
		return this._clientNameFallback.id === this._legacyClientId ? this._clientNameFallback.name : '';
	}

	@state() private _clientNameFallback = { id: 0, name: '' };

	private async _loadClientNameFallback(clientId: number): Promise<void> {
		try {
			const partnerId = await crosswalkClientToPartnerId(clientId);
			if (partnerId <= 0) return;
			const partner = (await api.getPartner(partnerId)) as { name?: string };
			if (partner?.name && clientId === this._legacyClientId) this._clientNameFallback = { id: clientId, name: partner.name };
		} catch {
			/* the box stays as it was */
		}
	}

	private _clientMatches(): LegacyClient[] {
		const q = (this._clientQuery ?? '').trim().toLowerCase();
		if (!q) return this._legacyClients;
		return this._legacyClients.filter(
			(c) => c.name.toLowerCase().includes(q) || (c.keyword ?? '').toLowerCase().includes(q),
		);
	}

	/**
	 * The library just picked, shown once as a confirmation and dismissed
	 * by the next click anywhere. Null when nothing to announce.
	 */
	@state() private _viewingAs: string | null = null;

	/**
	 * Whether the current route is still waiting for a library to be
	 * chosen. Communico staff have no tenant of their own, so every
	 * partner surface would otherwise render an error or an empty shell
	 * until a pick is made (Paul, 15 September 2026: "you should not be
	 * able to open anything until you have chosen a library").
	 *
	 * Delegates to navigation/library-gate.ts's libraryGateBlocks(), the
	 * exact same check app.ts's route guard uses to decide whether the
	 * page ever gets constructed in the first place, so this only ever
	 * renders the prompt for a route app.ts has already left un-mounted.
	 * Reads `this.path`/`this.userLevel` rather than `this._workspace`
	 * deliberately: `/kb` and `/community` must gate on the CURRENT
	 * `?view=` query, not on whichever workspace is sticky from an earlier
	 * navigation.
	 *
	 * Only ever true for the staff picker. A library's own user (level
	 * 3+) has an implied tenant and no picker to use, so gating them would
	 * lock them out of their own product.
	 */
	private _awaitingClient(): boolean {
		return libraryGateBlocks(this.path, window.location.search, this.userLevel, this._legacyClientId);
	}

	private _pickClient(id: number): void {
		// A session is pinned to one library, so switching libraries can
		// never carry it along — end it rather than leave the banner
		// naming a user who belongs to the library you just left. The new
		// pick is committed FIRST: it lives in sessionStorage, so it
		// survives the reload and the switch the operator actually asked
		// for still happens.
		if (id !== this._legacyClientId && currentImpersonationSession()) {
			pickLegacyClient(id);
			void stopImpersonation()
				.catch(() => { /* cleared locally regardless */ })
				.finally(() => window.location.reload());
			return;
		}
		const name = this._legacyClients.find((c) => c.id === id)?.name ?? '';
		pickLegacyClient(id);
		// Say whose data this now is. Communico staff can open any
		// library, so the one thing they must never have to guess is
		// which one they are looking at (Paul, 2026-07-29).
		this._viewingAs = name;
		this._clientQuery = null;
		this._clientOpen = false;
		this._clientActive = 0;
	}

	private _moveClientActive(delta: number, max: number): void {
		this._clientOpen = true;
		this._clientActive = Math.min(Math.max(this._clientActive + delta, 0), Math.max(max - 1, 0));
		void this.updateComplete.then(() => {
			this.renderRoot
				.querySelector(`#nav-client-opt-${this._clientActive}`)
				?.scrollIntoView({ block: 'nearest' });
		});
	}

	private _onClientKeydown(e: KeyboardEvent, shown: LegacyClient[]): void {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			this._moveClientActive(this._clientOpen ? 1 : 0, shown.length);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			this._moveClientActive(-1, shown.length);
		} else if (e.key === 'Enter') {
			const pick = shown[this._clientActive];
			if (this._clientOpen && pick) {
				e.preventDefault();
				this._pickClient(pick.id);
			}
		} else if (e.key === 'Escape') {
			// Swallow it — the shell's global Escape must not fire while the
			// list is open; a second press falls through to close the nav.
			if (this._clientOpen) e.stopPropagation();
			this._clientOpen = false;
			this._clientQuery = null;
		} else if (e.key === 'Tab') {
			this._clientOpen = false;
		}
	}

	private _renderClientPick() {
		const shown = this._clientMatches();
		const capped = shown.slice(0, 50);
		const liveMods = (c: LegacyClient) =>
			Object.entries(c.modules ?? {})
				.filter(([, on]) => on)
				.map(([m]) => m.charAt(0).toUpperCase() + m.slice(1))
				.join(' · ');
		return html`
			<div class="client-pick">
				<label for="nav-client">Client</label>
				<div class="cp-wrap">
					<input
						id="nav-client"
						type="text"
						role="combobox"
						autocomplete="off"
						aria-expanded=${this._clientOpen ? 'true' : 'false'}
						aria-controls="nav-client-list"
						aria-autocomplete="list"
						aria-activedescendant=${this._clientOpen && capped.length > 0
							? `nav-client-opt-${this._clientActive}`
							: ''}
						placeholder="Search libraries…"
						.value=${this._clientQuery ?? this._selectedClientName()}
						@focus=${(e: FocusEvent) => {
							this._clientOpen = true;
							this._clientActive = Math.max(
								this._legacyClients.findIndex((c) => c.id === this._legacyClientId),
								0,
							);
							(e.target as HTMLInputElement).select();
						}}
						@blur=${() => {
							this._clientOpen = false;
							this._clientQuery = null;
						}}
						@input=${(e: Event) => {
							this._clientQuery = (e.target as HTMLInputElement).value;
							this._clientOpen = true;
							this._clientActive = 0;
						}}
						@keydown=${(e: KeyboardEvent) => this._onClientKeydown(e, capped)} />
					${this._clientOpen
						? html`
								<div class="cp-list" id="nav-client-list" role="listbox" aria-label="Clients">
									${capped.map(
										(c, i) => html`
											<div
												class="cp-opt ${i === this._clientActive ? 'on' : ''}"
												id="nav-client-opt-${i}"
												role="option"
												aria-selected=${c.id === this._legacyClientId ? 'true' : 'false'}
												@mousedown=${(e: MouseEvent) => {
													// Before blur, so the click lands on a live list.
													e.preventDefault();
													this._pickClient(c.id);
												}}>
												<span class="cp-name">${c.name}</span>
												${liveMods(c) ? html`<span class="cp-mods">${liveMods(c)}</span>` : ''}
											</div>
										`,
									)}
									${capped.length === 0 ? html`<div class="cp-note">No match</div>` : ''}
									${shown.length > capped.length
										? html`<div class="cp-note">
												${shown.length - capped.length} more; keep typing
											</div>`
										: ''}
								</div>
							`
						: ''}
				</div>
			</div>
		`;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._unsubscribeLeaf?.();
		this._unsubscribeLeaf = null;
		if (this._leafTimer) clearTimeout(this._leafTimer);
		this._leafTimer = null;
		window.removeEventListener('cos:querychange', this._onTrailQueryChange);
		window.removeEventListener('popstate', this._onTrailQueryChange);
		window.removeEventListener(LEGACY_CLIENT_EVENT, this._onLegacyClientChanged as EventListener);
		window.removeEventListener(IMPERSONATION_EVENT, this._onImpersonationChanged as EventListener);
		window.removeEventListener('cos:chat-unread-sync', this._onChatUnreadSync as EventListener);
		window.removeEventListener('cos:chat-message', this._onChatFrame as EventListener);
		window.removeEventListener('cos:chat-status', this._onChatStatus as EventListener);
		window.removeEventListener('cos:action-center-changed', this._onActionCenterChanged);
		if (this._acPollTimer) clearInterval(this._acPollTimer);
		this._acPollTimer = null;
		window.removeEventListener('cos:open-create-partner', this._onOpenCreatePartner);
		window.removeEventListener('cos:open-quick-task', this._onOpenQuickTask);
		window.removeEventListener('cos:open-command-palette', this._onOpenCommandPalette as EventListener);
		window.removeEventListener('cos:request-nav-space', this._onNavSpaceRequest as EventListener);
		window.removeEventListener(OPEN_CLIENT_PICKER_EVENT, this._onOpenClientPicker);
		window.removeEventListener('cos:feedback:open', this._onFeedbackOpen);
		window.removeEventListener('cos:feedback:open-report', this._onFeedbackOpenReport as EventListener);
		window.removeEventListener('click', this._onWindowClickUser, true);
		document.removeEventListener('keydown', this._onDocKeydown);
		this._unregisterGlobals?.();
		this._unregisterGlobals = null;
		this._unsubPanel?.();
		this._unsubPanel = null;
		// Stage 38a — close the notification WebSocket.
		this._notifsWS?.disconnect();
		this._notifsWS = null;
		// Responsive Workstream C.
		this._unsubConnection?.();
		this._unsubConnection = null;
		window.removeEventListener('cos:querychange', this._onHelpRouteSignal);
		window.removeEventListener('popstate', this._onHelpRouteSignal);
	}

	// Stage 72 — the ONE keydown listener. Everything that was hand-rolled
	// here (Cmd+K palette, Cmd+/ agent) is now a registry binding; this
	// dispatches via shortcutRegistry.match(). The nav focus-trap below is
	// the only remaining bespoke handling (it's nav-modal-local, not a
	// shortcut).
	private _onDocKeydown = (e: KeyboardEvent) => {
		// Stage 81-polish — composedPath(), not e.target: a key pressed
		// inside a shadow-root-nested field retargets e.target to the shadow
		// host, so target.matches() misses the field and the guard leaks page
		// shortcuts mid-typing. See keyboard/inputGuard.ts.
		const inInput = eventTargetsField(e);
		// Input guard, applied BEFORE match() so a chord prefix ('g') typed
		// into a textarea never arms a chord. Only modifier-combos and Esc
		// can be escape-valve bindings, so only those reach match() while
		// focus is in a field; everything else is left to the input.
		const escapeValveCandidate = e.metaKey || e.ctrlKey || e.key === 'Escape';
		if (!inInput || escapeValveCandidate) {
			const binding = shortcutRegistry.match(e);
			if (binding) {
				if (inInput && !binding.allowInInputs) {
					// Belt-and-suspenders: a non-escape-valve combo binding in an
					// input is suppressed.
				} else {
					if (binding.preventDefault !== false) e.preventDefault();
					binding.handler(e);
					return;
				}
			}
		}

		if (!this._navOpen) return;
		if (e.key === 'Escape') {
			e.preventDefault();
			this._closeNav();
			return;
		}
		// Focus trap: cycle Tab within the nav while open.
		if (e.key === 'Tab') {
			const navEl = this.renderRoot.querySelector('nav');
			if (!navEl) return;
			const focusables = Array.from(
				navEl.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
			);
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			// renderRoot is the shadow root; activeElement may live there
			// or in light DOM depending on browser. Use the shadow root
			// when available.
			const root = this.renderRoot as ShadowRoot | Document;
			const active = (root.activeElement as HTMLElement | null) ?? null;
			if (e.shiftKey && active === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && active === last) {
				e.preventDefault();
				first.focus();
			}
		}
	};

	private _toggleNav(e: Event) {
		this._opener = e.currentTarget as HTMLElement;
		this._navOpen = !this._navOpen;
		if (this._navOpen) {
			// Focus the first nav link after the slide-in animation.
			requestAnimationFrame(() => {
				const first = this.renderRoot.querySelector<HTMLElement>('nav a');
				first?.focus();
			});
		}
	}

	private _closeNav() {
		this._navOpen = false;
		this._opener?.focus();
	}

	// Click on a nav LINK closes the mobile overlay (the click then
	// propagates to the parent's router-intercept handler). Narrowed to
	// anchors so the workspace switcher doesn't dismiss the overlay
	// mid-interaction.
	private _onNavLinkClick = (e: Event) => {
		const isLink = e.composedPath().some(
			(n) => n instanceof HTMLElement && n.tagName === 'A',
		);
		if (!isLink) return;
		if (this._navOpen) this._navOpen = false;
	};

	// Whether the "Admin" prime link shows at all — gated on the account
	// having at least one entitled admin-bearing product (RequireAdminRole
	// on /partner-admin's endpoints stays the real, server-side boundary;
	// this is presentation only). /partner-admin's own accordion nav lists
	// each entitled module — see cos-partner-admin-page.ts.
	private _adminModules(): PartnerAdminGroupInfo[] {
		if (this.userLevel >= 3 || this._workspace !== 'partner') return [];
		if (!ADMIN_ROLES.has(this.userRole)) return [];
		const products = this._circProducts;
		const hasCirc = products.includes('circulate');
		return PARTNER_ADMIN_GROUPS.filter(
			(g) =>
				// The product-neutral Library group (branches / hours /
				// pickup locations) is not an entitlement — every partner
				// has these settings (docs/branches-port-plan.md, D1).
				(UNGATED_ADMIN_PRODUCTS.has(g.product) || productEntitled(products, g.product)) &&
				g.sections.length > 0 &&
				// One-home rule: with Circulate entitled, Lend has no module
				// of its own — its sections ride the Circulate admin page.
				!(hasCirc && g.product === 'lend'),
		);
	}

	private _logout() {
		window.dispatchEvent(new CustomEvent('cos:auth:logout'));
	}

	// Stage 85a — initials for the avatar dropdown trigger. Up to two
	// initials from the display name; falls back to the email's first char.
	private _initials(): string {
		const name = (this.userName || '').trim();
		if (name) {
			const parts = name.split(/\s+/).filter(Boolean);
			const first = parts[0]?.[0] ?? '';
			const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
			return (first + last).toUpperCase();
		}
		return (this.userEmail || '?').trim().charAt(0).toUpperCase();
	}

	// Stage 85a — capture-phase outside-click closer for the user dropdown.
	// A click whose composedPath includes the .user-wrap (the trigger or a
	// menu item) is left alone; menu items close themselves. Anything else
	// closes the menu.
	private _onWindowClickUser = (e: Event) => {
		if (!this._userMenuOpen) return;
		const inWrap = e.composedPath().some(
			(n) => n instanceof HTMLElement && n.classList?.contains('user-wrap'),
		);
		if (!inWrap) this._userMenuOpen = false;
	};

	// Stage 19g (audit issue 034): the Stage/version tag is dev-only
	// affordance — partners shouldn't see it at the bottom of their
	// library-admin sidebar. import.meta.env.DEV is statically
	// replaced by Vite (true in dev, false in prod) so the prod
	// bundle ships `false ? ... : ''` which tree-shakes the dead
	// branch entirely.
	private _isDev(): boolean {
		return import.meta.env.DEV;
	}

	// The match itself lives in navigation/rail-active.ts, because the
	// breadcrumb trail has to ask exactly the same question: its first crumb
	// IS the row the rail lights (docs/design-system/breadcrumb-trail.md).
	// Two copies would drift; there was already a /dashboard alias, a
	// /billing exact-match arm and the per-row activeMatch overrides to keep
	// in step.
	private _isActive(href: string): boolean {
		return railHrefMatches(href, this.path);
	}

	/** Active state for a nav ROW, which may carry its own matcher when
	    two entries split one path prefix (Nurture's Audience/Marketing). */
	private _isActiveLink(n: NavLink): boolean {
		return railItemMatches(n, this.path);
	}

	private _onChatUnreadSync = (e: CustomEvent<{ total: number }>): void => {
		this._chatUnread = e.detail?.total ?? 0;
	};

	private _onChatFrame = (e: Event): void => {
		// The /chat page (when open) syncs precisely; this debounce covers
		// frames arriving while the user is elsewhere in the app.
		if (this._chatUnreadTimer) clearTimeout(this._chatUnreadTimer);
		this._chatUnreadTimer = setTimeout(() => void this._refreshChatUnread(), 800);
		this._maybeNotify((e as CustomEvent).detail ?? {});
	};

	private async _refreshChatUnread(): Promise<void> {
		// Chat is staff only. Until the user is known the shell is handed level 1 and no
		// id, so only a known Communico staff user asks; the badge's first read for staff
		// comes from updated() once the id arrives.
		if (this.userId <= 0 || this.userLevel !== 1) return;
		try {
			type Item = { id: number; unread: number; notify_level?: string; name?: string; kind?: string };
			const res = await api.getChatConversations() as { data?: Item[] };
			const items = res?.data ?? [];
			this._notifyMeta = new Map(items.map(i =>
				[i.id, { level: i.notify_level ?? 'all', name: i.name ?? '', kind: i.kind ?? 'channel' }]));
			// Muted conversations stay out of the badge — mute means mute.
			this._chatUnread = items.reduce((n, c) =>
				n + ((c.notify_level === 'mute') ? 0 : (c.unread ?? 0)), 0);
		} catch {
			// level-3 users / chat unavailable — no badge
		}
	}

	private _onActionCenterChanged = (): void => {
		void this._refreshActionCenterCount();
	};

	// The nav row only ever renders for level>=3 (partnerNav) or level<3
	// while the Partner workspace tab is active (see PARTNER_NAV_ACTION_CENTER's
	// push site) — polling for anyone else would just be a wasted 403/empty
	// round-trip against a row nobody can see.
	private async _refreshActionCenterCount(): Promise<void> {
		if (!(this.userLevel >= 3 || this._workspace === 'partner')) return;
		// Communico staff need a picked legacy client — without one the
		// endpoint 422s (it can't know which library's queue to count).
		const pickedClient = this.userLevel < 3 ? legacyClientId() || 0 : 0;
		if (this.userLevel < 3 && pickedClient <= 0) {
			this._acUnread = 0;
			return;
		}
		try {
			const counts = await api.getActionCenterCounts(
				pickedClient > 0 ? { hide_past: 1, client_id: pickedClient } : { hide_past: 1 },
			);
			this._acUnread = counts.unread_total ?? 0;
		} catch {
			// Not entitled (no Attend/Reserve) or the endpoint is unavailable —
			// no badge, not an error state.
		}
	}

	// --- 65a-4: platform-wide status + native chat notifications ---

	// Stage 74 — lazy-load the panel primitive on first open, then mirror
	// state. The element stays mounted after first use so the CSS slide
	// transition plays on subsequent opens/closes.
	private async _onPanelChange(): Promise<void> {
		const st = sidePanelManager.getState();
		if (st.opts && !this._panelMounted) {
			await import('./cos-side-panel.js');
			this._panelMounted = true;
		}
		this._panelState = st;
	}

	// Feedback 187 — the admin agent FAB sits at a fixed bottom-right
	// corner ABOVE the side panel (z-index, see .agent-fab above) so it
	// stays clickable while a panel is open. But that corner is also
	// where an open panel's own content (a form's Save button) can land,
	// and the FAB was covering it. Shift the FAB left of the panel's
	// width while one is open so it never overlaps the panel's content.
	// (Each width keeps its own literal var() call below, rather than
	// interpolating `width` into one var name — check-tokens.sh's
	// token-allowlist scan reads a template-interpolated var name as an
	// undefined token, since it can't see past the substitution.)
	private static readonly PANEL_WIDTH_VAR: Record<'sm' | 'md' | 'lg', string> = {
		sm: 'var(--cos-panel-width-sm)',
		md: 'var(--cos-panel-width-md)',
		lg: 'var(--cos-panel-width-lg)',
	};
	private _agentFabStyle(): string {
		if (!this._panelState.open) return '';
		const width = this._panelState.opts?.width ?? 'md';
		return `right: calc(${CosAppShell.PANEL_WIDTH_VAR[width]} + 24px)`;
	}

	protected willUpdate(changed: Map<string, unknown>): void {
		if (changed.has('path')) this._resetTrailForRoute();
		// Workspace route-sync: a navigation decides which side of the
		// split the nav shows. Partner-side paths flip to Partner; the
		// shared /kb follows its ?view= query; everything else is the
		// Communico platform. Only fires on a PATH change, so manually
		// switching tabs (no navigation) never fights it.
		if (changed.has('path') && this.userLevel < 3) {
			if (PARTNER_SIDE_PREFIXES.some((p) => this.path.startsWith(p))) {
				this._workspace = 'partner';
			} else if (this.path.startsWith('/kb') || this.path === '/community') {
				// Shared, workspace-ambiguous paths follow ?view=. `/community`
				// is matched EXACTLY (never .startsWith) so it can't catch
				// `/communications` (Comms Hub), which stays Communico-side.
				if (window.location.search.includes('view=partner')) this._workspace = 'partner';
				// plain (no ?view): keep whichever workspace the user is in
			} else {
				this._workspace = 'communico';
			}
		}
	}

	/** The rail scrolls on its own and a long Billing cluster can sit below
	 *  the fold, so bring the lit row into view. 'auto', never 'smooth': a
	 *  smooth scroll into a shadow scrollport is a silent no-op in Chrome. */
	private _revealActiveRailRow(): void {
		const row = this.renderRoot.querySelector('#primary-nav a.active') as HTMLElement | null;
		row?.scrollIntoView?.({ behavior: 'auto', block: 'nearest' });
	}

	protected updated(changed: Map<string, unknown>): void {
		// Stage 74 — close the side panel on any route change (its body
		// closes over a record that may no longer be on screen).
		if (changed.has('path')) sidePanelManager.close();
		if (changed.has('path')) this._revealActiveRailRow();
		if (changed.has('path') || changed.has('_workspace')) this._endImpersonationIfOutOfScope();
		if (
			(changed.has('_legacyClientId') || changed.has('_legacyClients')) &&
			this._legacyClientId > 0 &&
			this.userLevel < 3 &&
			this._clientNameFallback.id !== this._legacyClientId &&
			!this._legacyClients.some((c) => c.id === this._legacyClientId)
		) {
			void this._loadClientNameFallback(this._legacyClientId);
		}
		// Hydrate own status once the signed-in user is known (staff only).
		if (changed.has('userId') && this.userId > 0 && this.userLevel === 1 && !this._statusHydrated) {
			this._statusHydrated = true;
			void api.getChatStatus()
				.then((r) => { this._myStatus = (r as { status?: string })?.status ?? ''; })
				.catch(() => { /* chat unavailable — pill stays Active */ });
			void this._refreshChatUnread();
		}
		// The re-test link's "come back to the report" leg. Keyed on the
		// PROPERTY changing, not on `path` — @lit-labs/router's goto()
		// re-runs the matched route's enter() (where app.ts captures this)
		// even when navigating to the page the user is ALREADY on, so `path`
		// itself may not change even though a fresh capture just happened.
		// app.ts hands us a brand-new object every time for exactly that
		// reason (see its header comment) — this fires whenever THAT
		// reference changes, cold-load or in-app, same path or not.
		if (changed.has('pendingReportFocus') && this.pendingReportFocus) {
			void this._openFeedbackReportFromRetestLink(this.pendingReportFocus.id);
		}
		// Responsive Workstream A2 — auto-close the mobile nav overlay the
		// moment the viewport widens past the compact tier (replaces the old
		// matchMedia 'change' listener's identical auto-close), so the
		// persistent desktop sidebar takes over cleanly — e.g. a tablet
		// rotated to landscape while the menu was open. `_isNarrow` is a
		// getter over the ViewportController, not a tracked property, so the
		// transition is detected by comparing against the plain `_wasNarrow`
		// field rather than `changed.has(...)`.
		if (this._wasNarrow && !this._isNarrow && this._navOpen) {
			this._navOpen = false;
		}
		this._wasNarrow = this._isNarrow;
		// The document title is derived from the same trail, so it can never
		// disagree with what the header says (spec §5). Nothing set it before.
		this._syncDocumentTitle();
	}

	// ── Breadcrumb trail (docs/design-system/breadcrumb-trail.md) ─────────

	private _onTrailQueryChange = (): void => {
		this._trailSearch = window.location.search;
	};

	/** A new route: forget the old page's leaf, and start the 3s clock after
	 *  which a record route stops waiting and shows its fallback label. */
	private _resetTrailForRoute(): void {
		clearTrail();
		this._trailSearch = window.location.search;
		if (this._leafTimer) clearTimeout(this._leafTimer);
		this._leafTimer = null;
		this._leafPending = trailIsAwaitingLeaf(this.path, null);
		if (this._leafPending) {
			this._leafTimer = setTimeout(() => {
				this._leafPending = false;
				this._leafTimer = null;
			}, 3000);
		}
	}

	/** The rail as it renders right now, the user's own nav first, so the
	 *  trail's section crumb reads exactly what their rail reads. */
	private _trailSections(): TrailSection[] {
		const seen = new Set<string>();
		const out: TrailSection[] = [];
		for (const item of [...this._navItems(), ...ALL_RAIL_ITEMS]) {
			const key = item.href.split('?')[0];
			if (seen.has(key)) continue;
			seen.add(key);
			out.push({ href: item.href, label: item.label, icon: item.icon });
		}
		return out;
	}

	/** The leaf, but only the one published FOR the route being rendered. */
	private get _trailLeaf(): string | null {
		return trailLeafFor(this.path, this._trailSearch);
	}

	private get _trailCrumbLabels(): ReadonlyMap<string, string> {
		return crumbLabelsFor(this.path, this._trailSearch);
	}

	private _trailCrumbs(): Crumb[] {
		// Rollout flag (spec §11.1): 'off' renders no trail at all, for a
		// build that still wants the page-side crumbs on their own.
		if (import.meta.env.VITE_COS_SHELL_TRAIL === 'off') return [];
		const from = (window.history.state as { from?: string } | null)?.from ?? null;
		return resolveTrail(
			this.path,
			this._trailSearch,
			this._trailLeaf,
			from,
			this._trailSections(),
			this._trailCrumbLabels,
		);
	}

	private _syncDocumentTitle(): void {
		const crumbs = this._trailCrumbs();
		if (!crumbs.length) return;
		const section = crumbs[0].label;
		const current = crumbs[crumbs.length - 1].label;
		document.title = crumbs.length > 1
			? `${current} \u00b7 ${section} \u00b7 Communico One`
			: `${section} \u00b7 Communico One`;
	}

	private _renderTrail() {
		const crumbs = this._trailCrumbs();
		if (!crumbs.length) return nothing;
		return html`<cos-trail
			.crumbs=${crumbs}
			?compact=${this._isNarrow}
			?loading=${this._leafPending && !this._trailLeaf}
		></cos-trail>`;
	}

	private _onChatStatus = (e: CustomEvent<{ user_id: number; status: string }>): void => {
		// Another tab (or device) changed MY status — keep the pill honest.
		if (e.detail?.user_id === this.userId) this._myStatus = e.detail.status ?? '';
	};

	private async _setStatus(label: string): Promise<void> {
		this._statusOpen = false;
		const status = label === 'Active' ? '' : label;
		try {
			const r = await api.setChatStatus(status) as { status?: string };
			this._myStatus = r?.status ?? status;
		} catch {
			// validation (too long) or offline — leave the pill unchanged
		}
	}

	private _customStatus(): void {
		const v = window.prompt('Set a status (empty = Active)', this._myStatus);
		if (v === null) { this._statusOpen = false; return; }
		void this._setStatus(v.trim() === '' ? 'Active' : v.trim());
	}

	// Native (OS-level) notification for messages arriving while the
	// user is elsewhere. REST stays the source of truth — this is
	// purely an attention nudge, gated by the per-conversation pref.
	private _maybeNotify(msg: { conversation_id?: number; author_id?: number; author_name?: string; body?: string }): void {
		if (!msg || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
		if (this.userId > 0 && msg.author_id === this.userId) return; // own message
		const meta = this._notifyMeta.get(msg.conversation_id ?? -1);
		const level = meta?.level ?? 'all';
		if (level === 'mute') return;
		if (level === 'mentions' && meta?.kind === 'channel') {
			const first = (this.userName || '').split(' ')[0].toLowerCase();
			const body = (msg.body ?? '').toLowerCase();
			const mentioned = (first && body.includes('@' + first)) || body.includes('@all') || body.includes('@channel');
			if (!mentioned) return;
		}
		// Do-not-disturb silences everything except nothing.
		if (this._myStatus.toLowerCase() === 'do not disturb') return;
		// Looking at /chat with the window focused — the page itself is
		// the notification.
		if (this.path === '/conversations' && document.hasFocus()) return;
		const title = meta?.kind === 'dm' ? (msg.author_name || 'New message') : `#${meta?.name || 'chat'}`;
		const text = meta?.kind === 'dm'
			? (msg.body ?? '')
			: `${msg.author_name || 'Someone'}: ${msg.body ?? ''}`;
		try {
			const n = new Notification(title, { body: text.slice(0, 140), tag: `cos-chat-${msg.conversation_id}` });
			n.onclick = () => {
				window.focus();
				window.history.pushState({}, '', '/conversations');
				window.dispatchEvent(new PopStateEvent('popstate'));
				n.close();
			};
		} catch {
			// constructor can throw on some platforms — never break the app
		}
	}

	// A plain nav row (read mode). Every row, including "Admin", is a
	// plain link — /partner-admin's own accordion nav is how a module is
	// picked once there (the flyout that used to do that from the rail
	// was retired 2026-07-29). Shared by the Communico nav map + the
	// Partner grouped render.
	private _renderNavRow(n: NavLink) {
		// Responsive Workstream B — a route gated at the CURRENT viewport
		// tier gets a small marker, never a hidden row (spec §7:
		// "discoverability over mystery"). Most rows are unlisted in the
		// registry and resolve 'full' — zero cost, no marker.
		const gate = capabilityFor(n.href, this._viewport.tier);
		return html`
			<a href=${n.href}
				class=${this._isActiveLink(n) ? 'active' : ''}
				data-label=${n.subtitle ? `${n.label} · ${n.subtitle}` : n.label}
				aria-current=${this._isActiveLink(n) ? 'page' : 'false'}>
				<cos-icon class="icon" name=${n.icon} size="20"></cos-icon>
				<span class="nav-label"
					>${n.label}${n.subtitle
						? html`<span class="nav-sub">${n.subtitle}</span>`
						: ''}</span
				>
				${gate.level !== 'full'
					? html`<cos-icon
							class="nav-gate-marker"
							name="monitor"
							size="14"
							title=${gate.note ?? 'Needs a larger screen'}></cos-icon>`
					: ''}
				${n.href === '/conversations' && this._chatUnread > 0
					? html`<span class="nav-badge">${this._chatUnread > 99 ? '99+' : this._chatUnread}</span>`
					: ''}
				${n.href === '/action-center' && this._acUnread > 0
					? html`<span class="nav-badge">${this._acUnread > 99 ? '99+' : this._acUnread}</span>`
					: ''}
			</a>`;
	}

	// ---- Partner-workspace nav customization (reorder + optional groups) ----

	/** The stable per-module key used in the saved nav_layout doc. */
	private _partnerReorderKey(n: NavLink): string {
		return n.product ?? (n.href.startsWith('/kb') ? 'kb' : n.href);
	}

	/** Split the partner nav into the reorderable set (products + KB) and
	    the pinned "Admin" link (permission-gated chrome, never grouped). */
	private _partnerNavParts(): { admin: NavLink | null; reorder: NavLink[] } {
		const items = this._navItems();
		return {
			admin: items.find((n) => n.adminHub) ?? null,
			reorder: items.filter((n) => !n.adminHub),
		};
	}

	/** The current reconciled sections (saved layout ∩ live entitlement). */
	private _currentNavSections(): NavSection[] {
		const { reorder } = this._partnerNavParts();
		return applyNavLayout(reorder.map((n) => this._partnerReorderKey(n)), this._navLayout);
	}

	/** Optimistically apply + persist a new arrangement. */
	private _commitNavSections(sections: NavSection[]): void {
		const doc = docFromSections(sections);
		if (this._navLayout?.hidden?.length) doc.hidden = [...this._navLayout.hidden];
		this._commitNavDoc(doc);
	}

	private _commitNavDoc(doc: NavLayoutDoc): void {
		this._navLayout = doc;
		void api.setMyPreference('nav_layout', doc).catch(() => {
			/* optimistic — a failed write just reverts on next load */
		});
	}

	/** The reconciled hidden-key set for the current reorderable items. */
	private _navHiddenSet(): Set<string> {
		const { reorder } = this._partnerNavParts();
		return hiddenNavKeys(reorder.map((n) => this._partnerReorderKey(n)), this._navLayout);
	}

	/** Hide a module from the rail. Its position is preserved for restore. */
	private _navHide(key: string): void {
		const base = this._navLayout ?? docFromSections(this._currentNavSections());
		this._commitNavDoc(hideNavKey(base, key));
	}

	/** Restore a hidden module to its saved position. */
	private _navShow(key: string): void {
		if (!this._navLayout) return;
		this._commitNavDoc(showNavKey(this._navLayout, key));
	}

	private _renderPartnerNav() {
		const { admin, reorder } = this._partnerNavParts();
		const byKey = new Map(reorder.map((n) => [this._partnerReorderKey(n), n]));
		const sections = applyNavLayout(reorder.map((n) => this._partnerReorderKey(n)), this._navLayout);
		const hidden = this._navHiddenSet();
		const edit = this._navEdit;
		return html`
			<div class="nav-custom-bar ${edit ? 'editing' : ''}">
				${edit
					? html`
						<button type="button" class="nc-link" @click=${this._navAddGroup}>
							<cos-icon name="plus" size="14"></cos-icon> New group
						</button>
						<span class="nc-spacer"></span>
						<button type="button" class="nc-link" @click=${this._navReset}>Reset</button>
						<button type="button" class="nc-done" @click=${() => { this._navEdit = false; }}>Done</button>`
					: html`
						<button type="button" class="nc-link" @click=${() => { this._navEdit = true; }}>
							<cos-icon name="settings" size="14"></cos-icon> Customize
						</button>`}
			</div>
			${sections.map((sec, i) => this._renderNavSection(sec, i, byKey, edit, hidden))}
			${edit ? this._renderNavHiddenList(byKey, hidden) : ''}
			${admin ? this._renderNavRow(admin) : ''}`;
	}

	private _renderNavSection(sec: NavSection, sIdx: number, byKey: Map<string, NavLink>, edit: boolean, hidden: Set<string>) {
		const rows = sec.items
			.map((k) => byKey.get(k))
			.filter((n): n is NavLink => !!n && !hidden.has(this._partnerReorderKey(n)));
		if (!edit && rows.length === 0) return '';
		const header = sec.name && (edit || rows.length > 0)
			? edit
				? html`
					<div class="nc-group-head">
						<input class="nc-group-name" .value=${sec.name} maxlength="40"
							aria-label="Group name" placeholder="Group name"
							@change=${(e: Event) => this._navRenameGroup(sIdx, (e.target as HTMLInputElement).value)} />
						<button type="button" class="nc-group-del" title="Remove group" aria-label="Remove group"
							@click=${() => this._navRemoveGroup(sIdx)}>
							<cos-icon name="x" size="14"></cos-icon>
						</button>
					</div>`
				: html`<div class="group-label">${sec.name}</div>`
			: '';
		// A section is a drop target (append / empty group). It highlights
		// only when the cursor is over its own chrome, not over a row (the
		// row handler stops propagation + owns the precise before/after line).
		const sectionDropping = edit && this._navDropSection === sIdx;
		return html`
			<div class="nav-section ${edit ? 'editing' : ''} ${sectionDropping ? 'dropping' : ''}"
				@dragover=${(e: DragEvent) => this._onNavSectionDragover(e, sIdx)}
				@dragleave=${(e: DragEvent) => this._onNavSectionDragleave(e, sIdx)}
				@drop=${(e: DragEvent) => this._onNavSectionDrop(e, sIdx)}>
				${header}
				${rows.map((n) => (edit ? this._renderEditRow(n) : this._renderNavRow(n)))}
				${edit && rows.length === 0 ? html`<div class="nc-empty">Drop a module here</div>` : ''}
			</div>`;
	}

	private _renderEditRow(n: NavLink) {
		const key = this._partnerReorderKey(n);
		const dragging = this._navDragKey === key;
		// Insertion line: shown on the hovered row, above (before) or below
		// (after) per the cursor half — never on the row being dragged.
		const dropHere = this._navDropKey === key && !dragging;
		const cls = [
			'nav-edit-row',
			dragging ? 'dragging' : '',
			dropHere ? (this._navDropAfter ? 'drop-after' : 'drop-before') : '',
		]
			.filter(Boolean)
			.join(' ');
		return html`
			<div class=${cls} draggable="true" data-key=${key}
				@dragstart=${(e: DragEvent) => this._onNavDragStart(e, key)}
				@dragend=${this._onNavDragEnd}
				@dragover=${(e: DragEvent) => this._onNavRowDragover(e, key)}
				@drop=${(e: DragEvent) => this._onNavRowDrop(e, key)}>
				<cos-icon class="nc-grip" name="move" size="16"></cos-icon>
				<cos-icon class="icon" name=${n.icon} size="20"></cos-icon>
				<span class="nav-label"
					>${n.label}${n.subtitle ? html`<span class="nav-sub">${n.subtitle}</span>` : ''}</span
				>
				<button type="button" class="nc-row-hide" title="Hide from sidebar" aria-label="Hide ${n.label} from sidebar"
					@mousedown=${(e: Event) => e.stopPropagation()}
					@click=${(e: Event) => { e.stopPropagation(); this._navHide(key); }}>
					<cos-icon name="eye-off" size="14"></cos-icon>
				</button>
			</div>`;
	}

	/** Hidden-but-restorable modules, listed at the bottom of Customize mode
	    (hidden never means gone — see docs/control-panel-feedback-track-a.md S1). */
	private _renderNavHiddenList(byKey: Map<string, NavLink>, hidden: Set<string>) {
		if (hidden.size === 0) return '';
		const rows = [...hidden].map((k) => byKey.get(k)).filter((n): n is NavLink => !!n);
		if (rows.length === 0) return '';
		return html`
			<div class="nav-section nc-hidden-list">
				<div class="group-label">Hidden</div>
				${rows.map((n) => {
					const key = this._partnerReorderKey(n);
					return html`
						<div class="nav-edit-row nc-hidden-row">
							<cos-icon class="icon" name=${n.icon} size="20"></cos-icon>
							<span class="nav-label">${n.label}</span>
							<button type="button" class="nc-row-show" @click=${() => this._navShow(key)}>
								<cos-icon name="eye" size="14"></cos-icon> Show
							</button>
						</div>`;
				})}
			</div>`;
	}

	private _clearNavDrop(): void {
		if (this._navDropKey !== null || this._navDropSection !== null) {
			this._navDropKey = null;
			this._navDropSection = null;
			this.requestUpdate();
		}
	}
	private _onNavDragStart(e: DragEvent, key: string): void {
		this._navDragKey = key;
		if (e.dataTransfer) {
			e.dataTransfer.setData('text/plain', key);
			e.dataTransfer.effectAllowed = 'move';
		}
		this.requestUpdate();
	}
	private _onNavDragEnd = (): void => {
		this._navDragKey = null;
		this._clearNavDrop();
		this.requestUpdate();
	};
	private _onNavRowDragover(e: DragEvent, targetKey: string): void {
		if (!this._navDragKey) return;
		e.preventDefault();
		e.stopPropagation(); // this row owns the drop line; suppress the section highlight
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		if (targetKey === this._navDragKey) {
			this._clearNavDrop(); // no indicator over the row being dragged
			return;
		}
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
		const after = e.clientY - rect.top > rect.height / 2;
		if (this._navDropKey !== targetKey || this._navDropAfter !== after || this._navDropSection !== null) {
			this._navDropKey = targetKey;
			this._navDropAfter = after;
			this._navDropSection = null;
			this.requestUpdate();
		}
	}
	private _onNavRowDrop(e: DragEvent, targetKey: string): void {
		e.preventDefault();
		e.stopPropagation(); // don't also fire the section (append) drop
		const key = this._navDragKey;
		const after = this._navDropAfter;
		this._clearNavDrop();
		if (!key || key === targetKey) return;
		const cur = this._currentNavSections();
		this._commitNavSections(after ? moveNavItemAfter(cur, key, targetKey) : moveNavItemBefore(cur, key, targetKey));
	}
	private _onNavSectionDragover(e: DragEvent, sIdx: number): void {
		if (!this._navDragKey) return;
		e.preventDefault();
		// Reached only over the section's own chrome (rows stopPropagation).
		if (this._navDropSection !== sIdx || this._navDropKey !== null) {
			this._navDropSection = sIdx;
			this._navDropKey = null;
			this.requestUpdate();
		}
	}
	private _onNavSectionDragleave(e: DragEvent, sIdx: number): void {
		// Only clear when the pointer truly left this section (not a child).
		const related = e.relatedTarget as Node | null;
		if (this._navDropSection === sIdx && !(e.currentTarget as HTMLElement).contains(related)) {
			this._navDropSection = null;
			this.requestUpdate();
		}
	}
	private _onNavSectionDrop(e: DragEvent, sIdx: number): void {
		e.preventDefault();
		const key = this._navDragKey;
		this._clearNavDrop();
		if (!key) return;
		this._commitNavSections(moveNavItemToSectionEnd(this._currentNavSections(), key, sIdx));
	}

	private _navAddGroup = (): void => {
		this._commitNavSections(addNavGroup(this._currentNavSections()));
	};
	private _navRenameGroup(idx: number, name: string): void {
		this._commitNavSections(renameNavGroup(this._currentNavSections(), idx, name.trim()));
	}
	private _navRemoveGroup(idx: number): void {
		this._commitNavSections(removeNavGroup(this._currentNavSections(), idx));
	}
	private _navReset = (): void => {
		// A full reset — order, groups AND hidden state all go back to
		// default (unlike a plain reorder/hide, which each preserve the
		// other). Bypasses _commitNavSections' hidden carry-over on purpose.
		const { reorder } = this._partnerNavParts();
		this._commitNavDoc(docFromSections(defaultNavSections(reorder.map((n) => this._partnerReorderKey(n)))));
	};

	/** The Partner workspace rail: the canonical, owner-pinned order. Shared by
	    the staff Partner tab and the library-login rail (_navItems) so there is
	    exactly one copy of the order. */
	private _partnerWorkspaceItems(): NavLink[] {
		// Partner workspace: the products this account is entitled to
		// (one-home rule — Lend standalone only without Circulate),
		// the partner-visible slice of the KB, and the per-product
		// Admin section (permission-gated, like the Communico one).
		//
		// THE ORDER BELOW IS PINNED (Paul, 2026-08-18) — this is the
		// canonical rail, top to bottom. Entitlement gates only decide
		// whether a row DROPS OUT, never where it sits. This order is
		// also what the nav-customization Reset restores and what the
		// reconcile appends in for customized users. When adding a new
		// entry: do NOT pick a slot yourself and do NOT append to the
		// end — ask Paul where it goes, then place it here.
		const items: NavLink[] = [];
		// Action Center leads — "what's waiting on you" before anything
		// else. Gated on ANY of the products it queues (Attend mediation,
		// Reserve approvals, Schedule appointment requests) being entitled.
		if (this._circProducts.includes('attend') || this._circProducts.includes('reserve') || this._circProducts.includes('schedule')) {
			items.push(PARTNER_NAV_ACTION_CENTER);
		}
		// Desk (docs/desk-plan.md) — the front-of-house surface a
		// staffer has open all day. Ungated like Communications/Media
		// below: not a purchased product, its task lanes derive their
		// own entitlement.
		items.push(PARTNER_NAV_DESK);
		// Email — the shared-inbox surface, in the daily-drive cluster
		// (Paul, 2026-08-18: "right after Desk").
		if (this._circProducts.includes('nurture')) items.push(PARTNER_NAV_EMAIL);
		// Lend is its own product (Paul, 2026-09-06, supersedes the
		// one-home-tab ruling): its rail entry is independent of
		// Circulate's, always shown when entitled, and PINNED directly
		// after it.
		if (this._circProducts.includes('circulate')) items.push(PARTNER_NAV_CIRCULATE);
		if (this._circProducts.includes('lend')) items.push(PARTNER_NAV_LEND);
		if (this._circProducts.includes('reserve')) items.push(PARTNER_NAV_RESERVE);
		if (this._circProducts.includes('schedule')) items.push(PARTNER_NAV_SCHEDULE);
		if (this._circProducts.includes('attend')) items.push(PARTNER_NAV_ATTEND);
		// Outreach: seated directly under Attend (Paul, 2026-08-23).
		// This slot is PINNED; do not move it when adding future rows.
		if (this._circProducts.includes('outreach')) items.push(PARTNER_NAV_OUTREACH);
		if (this._circProducts.includes('preserve')) items.push(PARTNER_NAV_PRESERVE);
		if (this._circProducts.includes('broadcast')) items.push(PARTNER_NAV_BROADCAST);
		items.push(PARTNER_NAV_COLLECTIONS);
		items.push(PARTNER_NAV_EXPLORE);
		// Audience + Marketing + Surveys sit together, Surveys directly
		// after Marketing (Paul, 2026-08-20); the Boardroom deliberately
		// does NOT sit here — Paul's arrangement seats it with the
		// outward-facing surfaces further down.
		if (this._circProducts.includes('nurture')) {
			items.push(PARTNER_NAV_AUDIENCE);
			items.push(PARTNER_NAV_MARKETING);
			items.push(PARTNER_NAV_SURVEYS);
		}
		items.push(PARTNER_NAV_MEDIA);
		if (this._circProducts.includes('roster')) items.push(PARTNER_NAV_ROSTER);
		// One rail entry opens the shell that hosts both Measure's pages and
		// Thrive's. Every library with Measure gets in, and so does one with
		// Thrive; each page inside then shows only if the library bought what it
		// needs. Thrive is NOT derived from Measure (Paul, 1 November 2026), so a
		// library with Measure alone sees the entry under Measure's own name
		// rather than as a product it did not buy.
		// Measure is every library's (Paul, 1 November 2026), so the entry is
		// always there; only its name depends on a Thrive purchase.
		const hasThrive = this._circProducts.includes('thrive');
		if (hasThrive) items.push(PARTNER_NAV_THRIVE);
		else items.push({ ...PARTNER_NAV_THRIVE, label: PARTNER_MODULE_LABELS.measure.main });
		items.push(PARTNER_NAV_REPORTS);
		items.push(PARTNER_NAV_ANNUAL_REPORT);
		// Every library, regardless of entitlement — the delivery rail
		// underneath whichever products they do have.
		items.push(PARTNER_NAV_COMMS);
		// Every library: the Boardroom ships in Communico One, so it is not
		// gated on the Nurture purchase (Paul, 31 October 2026).
		items.push(PARTNER_NAV_BOARDROOM);
		// Community — pushed unconditionally like Comms/Media (not a
		// purchased product), gated only on the module being enabled.
		if (this._communityEnabled) items.push(PARTNER_NAV_COMMUNITY);
		items.push(PARTNER_NAV_KB);
		// One prime "Admin" link owns every module's admin; rendered
		// pinned at the rail bottom by _partnerNavParts regardless of
		// push position. It only appears when the account has an
		// admin-bearing module AND the user is an admin role
		// (server-side RequireAdminRole is the real boundary — this is
		// presentation).
		if (this._adminModules().length > 0) items.push(PARTNER_NAV_ADMIN);
		return items;
	}

	private _navItems(): NavLink[] {
		if (this.userLevel >= 3) {
			// Real library-staff logins get the member Community experience
			// alongside their partner nav (community's role model gates the
			// rest). Shown only when the module is enabled for the deployment.
			//
			// Dashboard leads, then the rows the library may see (the same
			// pinned order the staff Partner tab uses, narrowed by
			// LIBRARY_RAIL_ALLOW so no dead link ships), then the remaining
			// support links they always had.
			const [dashboard, ...support] = partnerNav(this.userPartnerId);
			const admin = this.userLevel <= 3;
			const owned = this._partnerWorkspaceItems().filter((n) =>
				n.product !== undefined
					? LIBRARY_RAIL_ALLOW.products.has(n.product) || (admin && LIBRARY_RAIL_ALLOW.adminProducts.has(n.product))
					: LIBRARY_RAIL_ALLOW.hrefs.has(n.href) || (admin && LIBRARY_RAIL_ALLOW.adminHrefs.has(n.href)));
			const items = [dashboard, ...owned, ...support];
			if (this._communityEnabled) items.push(PARTNER_NAV_COMMUNITY);
			return items;
		}
		if (this._workspace === 'partner') return this._partnerWorkspaceItems();
		// Communico workspace: the ops platform. The product surfaces
		// live on the Partner side now; Community shows only when the
		// module is enabled for this deployment.
		//
		// Billing moved off the level-1 staff gate (21 September 2026):
		// RequireBillingAccess now checks a hardcoded named allowlist in
		// the backend, and the frontend carries no copy of that list. It
		// only has the one flag app.ts computed from /auth/me's
		// can_access_billing, which is the same middleware.CanAccessBilling
		// the route itself gates on. The acts that freeze the books stay
		// super_admin inside the pages.
		const base = STAFF_NAV.filter((n) =>
			n.href !== '/circulate' &&
			n.href !== '/lend' &&
			(n.href !== '/community' || this._communityEnabled) &&
			(this.canAccessBilling || (n.href !== '/billing' && !n.href.startsWith('/billing/'))));
		return ADMIN_ROLES.has(this.userRole) ? [...base, ADMIN_NAV_ITEM] : base;
	}

	/**
	 * Impersonation is confined to the Partner workspace (Paul,
	 * 2026-08-18). Leaving it drops the session and puts the operator back
	 * on their own login rather than letting a borrowed identity follow
	 * them around the platform.
	 *
	 * Two ways out, and both matter. Switching back to the Communico side
	 * is the deliberate one. Landing on a Communico-only route is the
	 * accidental one — a bookmark, the back button, a link out of an
	 * email — and it is the case a workspace check alone would miss,
	 * because the workspace route-sync in willUpdate is gated on
	 * `userLevel < 3` and the effective level while impersonating is the
	 * TARGET's.
	 *
	 * Ending is best-effort and always clears locally: if the server call
	 * fails we must not leave the operator believing they are themselves
	 * while still holding a session id.
	 */
	private _endImpersonationIfOutOfScope(): void {
		const session = currentImpersonationSession();
		if (!session) return;
		const outOfScope =
			this._workspace !== 'partner' ||
			COMMUNICO_ONLY_PREFIXES.some((p) => this.path.startsWith(p));
		if (!outOfScope) return;
		// Reload for the same reason the banner's own Stop does: the page
		// on screen was rendered against the target's scope, so re-fetch
		// everything under the real identity instead of leaving stale,
		// target-scoped data behind.
		void stopImpersonation()
			.catch(() => { /* cleared locally regardless */ })
			.finally(() => window.location.reload());
	}

	private _setWorkspace(w: Workspace): void {
		this._workspace = w;
		// Recovery path for the client picker: the boot-time load is
		// once-per-session and swallows failures, so re-attempt when the
		// user enters the Partner workspace with an empty list (the
		// service never caches failures, so this retry is real). Level < 3
		// (not just level 1): level-2 Communico staff need the same picker.
		if (w === 'partner' && this.userLevel < 3 && this._legacyClients.length === 0) {
			void this._loadLegacyClients();
		}
	}

	// In-app help — dynamic-imports the module-agnostic panel engine (+ the
	// thin Circulate subclass) once per session. Fired from connectedCallback
	// (see the call site's comment), not gated behind a click: this is a
	// persistent chrome affordance every route needs, so it can't wait for
	// a first interaction the way the agent panel/palette do below.
	private async _mountHelp(): Promise<void> {
		if (this._helpMounted) return;
		await Promise.all([import('./cos-help-panel.js'), import('./cos-circulate-help.js')]);
		this._helpMounted = true;
	}

	private _onHelpRouteSignal = (): void => {
		if (this.path.startsWith('/circulate')) this.requestUpdate();
	};

	// Circulate's currently-selected workspace tab (Circulation/Cataloging/
	// Transfers/…, of its eleven) — read straight off the URL at render
	// time rather than cached, so `cos:querychange`/`popstate` re-renders
	// (see _onHelpRouteSignal) are all that's needed to pick up a change;
	// there's no stale-state case to worry about. Mirrors
	// cos-circulate-page.ts's own `_moduleFromURL` default.
	private _circulateActiveModule(): string {
		return new URLSearchParams(window.location.search).get('m') || 'circulation';
	}

	// The one shared help launcher/panel every authenticated route gets.
	// Circulate is routed through its subclass (own copy, consent-prompt
	// text, and `active-module`-gated availability — see that file); every
	// other module gets the base panel fed whatever HELP_SCREEN_CONFIG has
	// for it (or the empty defaults) and an `available` flag from whether
	// helpgen actually publishes that module's content.
	private _renderHelp() {
		const module = helpModuleForPath(this.path);
		if (module === 'circulate') {
			return html`<cos-circulate-help
				active-module=${this._circulateActiveModule()}
				.userLevel=${this.userLevel}
			></cos-circulate-help>`;
		}
		const cfg = HELP_SCREEN_CONFIG[module];
		return html`<cos-help-panel
			module=${module}
			.available=${KNOWN_HELP_MODULES.has(module)}
			.userLevel=${this.userLevel}
			.screenLabels=${cfg?.labels ?? EMPTY_HELP_MAP}
			.screenNavHooks=${cfg?.navHooks ?? EMPTY_HELP_MAP}
			.startPage=${helpStartPageForPath(this.path)}
		></cos-help-panel>`;
	}

	// Stage 30 — admin gate for the agent FAB + Cmd+/ shortcut. Mirrors
	// the ADMIN_ROLES set used for the nav-item gate; staff with non-
	// admin roles (engineering, support, cs, sales, qa, etc.) don't
	// see the FAB at all. Backend RequireAdminRole is the authoritative
	// gate; this is presentation-only.
	private _isAdmin(): boolean {
		return this.userLevel === 1 && ADMIN_ROLES.has(this.userRole);
	}

	// First click on the FAB resolves the panel module (~6–8 kB gzip
	// chunk). Subsequent clicks just flip the open flag. The panel
	// preserves its own state so closing + reopening keeps the
	// conversation in place.
	private async _toggleAgent() {
		if (!this._agentMounted) {
			await import('./cos-agent-panel.js');
			this._agentMounted = true;
		}
		this._agentOpen = !this._agentOpen;
		// Restore focus to the FAB on close so the keyboard user lands
		// somewhere predictable.
		if (!this._agentOpen) {
			queueMicrotask(() => {
				const fab = this.renderRoot.querySelector<HTMLElement>('.agent-fab');
				fab?.focus();
			});
		}
	}

	// Stage 71 — open the command palette, lazy-loading it (+ the command
	// registry + create-partner modal) on first use.
	private async _openPalette() {
		if (!this._paletteMounted) {
			await Promise.all([
				import('./cos-command-palette.js'),
				import('./cos-create-partner-modal.js'),
			]);
			this._paletteMounted = true;
		}
		this._paletteOpen = true;
	}

	private _onPaletteClose = () => {
		this._paletteOpen = false;
	};

	// Stage 72 — lazy-load + open the `?` shortcut overlay. Mounted only
	// while open so the modal lifecycle (focus capture/restore) rides
	// connect/disconnect.
	private async _openOverlay() {
		if (this._overlayOpen) return;
		if (!this._overlayMounted) {
			await import('./cos-shortcut-overlay.js');
			this._overlayMounted = true;
		}
		this._overlayOpen = true;
	}

	// Global Esc fallback — close the agent panel / nav overlay. (The `?`
	// overlay and the palette trap their own Esc, so this only runs when
	// neither is up.)
	private _closeOverlays() {
		if (this._agentOpen) this._agentOpen = false;
		if (this._navOpen) this._closeNav();
	}

	// The palette's "Create partner" action dispatches this; open the modal.
	private _onOpenCreatePartner = () => {
		this._createPartnerOpen = true;
	};

	// The palette's "New task" action dispatches this; open the quick-capture.
	private _onOpenQuickTask = () => {
		// Marcie R4 — Cmd+K "New task" now opens the SAME full modal as the
		// "+ New task" button + record tiles (was the bare single-field
		// cos-quick-task, which couldn't set assignee/due/urgency/detail).
		void import('./cos-task-modal.js');
		this._quickTaskOpen = true;
	};

	// fleet/desk-board: Desk's `/` affordance (branch bar button, rail
	// footer button, and the page's own page-scope `/` keyboard shortcut)
	// dispatches this to reuse the global Cmd+K palette rather than a
	// second one.
	private _onOpenCommandPalette = () => {
		void this._openPalette();
	};

	// The Cmd+Shift+F global shortcut dispatches this; the widget itself
	// owns its lazy-mount + open state, so the shell just forwards the ask.
	private _onFeedbackOpen = () => {
		this.renderRoot.querySelector('cos-feedback-widget')?.open();
	};

	// Feedback #10 — the bell/notifications-page dispatch this for a
	// feedback.* row click (see the addEventListener comment above); forward
	// to the exact same "open focused on a report" leg the re-test email
	// link uses, rather than duplicating it.
	private _onFeedbackOpenReport = (e: Event) => {
		const id = (e as CustomEvent<{ id: number }>).detail?.id;
		if (typeof id === 'number') void this._openFeedbackReportFromRetestLink(id);
	};

	// The re-test link's report-focus id (see the `pendingReportFocus`
	// property + the updated() hook above). By the time updated() fires
	// the DOM has already been patched for this render, so
	// <cos-feedback-widget> already exists — the extra updateComplete await
	// is defensive, not load-bearing.
	private async _openFeedbackReportFromRetestLink(id: number): Promise<void> {
		await this.updateComplete;
		await this.renderRoot.querySelector('cos-feedback-widget')?.openReport(id);
	}

	private _onAgentClose = () => {
		this._agentOpen = false;
		queueMicrotask(() => {
			const fab = this.renderRoot.querySelector<HTMLElement>('.agent-fab');
			fab?.focus();
		});
	};

	// A docked side panel asking for room (cos:request-nav-space). Borrow the
	// rail only if it's currently expanded — if the user already collapsed it
	// there's nothing to borrow, and nothing to hand back afterwards.
	//
	// PER-SOURCE (Paul, 2026-08-26 — "the help panel is flaky about
	// minimising the menu"): TWO panels dispatch this event (help +
	// feedback), and a single borrowed flag let one panel's release hand
	// the rail back while the OTHER was still docked open — the exact
	// open-close-a-few-times flake. Each sender names itself; the rail
	// stays borrowed while ANY source still holds it, and repeated asks
	// from one source are idempotent (Set semantics). A legacy dispatch
	// with no source shares the "default" slot, which preserves the old
	// single-panel behaviour byte-for-byte.
	private _navBorrowSources = new Set<string>();
	private _onNavSpaceRequest = (e: Event) => {
		const detail = (e as CustomEvent<{ collapse?: boolean; source?: string }>).detail;
		const want = !!detail?.collapse;
		const source = detail?.source || 'default';
		if (want) {
			this._navBorrowSources.add(source);
			if (!this.collapsed) {
				this.collapsed = true;
				this._navBorrowed = true;
			}
		} else {
			this._navBorrowSources.delete(source);
			if (this._navBorrowSources.size === 0 && this._navBorrowed) {
				this.collapsed = false;
				this._navBorrowed = false;
			}
		}
	};

	// Toggle the desktop icon-rail and remember the choice.
	private _toggleCollapsed = () => {
		this.collapsed = !this.collapsed;
		// The user just took ownership of the rail — whatever a docked panel
		// borrowed is theirs now, so don't override this on close.
		this._navBorrowed = false;
		this._navBorrowSources.clear();
		try {
			localStorage.setItem('cos-nav-collapsed', this.collapsed ? '1' : '0');
		} catch { /* storage unavailable — in-memory only */ }
	};

	// Level 1 and 2 are Communico staff, 3 is a library's admin, 4 its staff.
	private _levelLabel(): string {
		if (this.userLevel >= 4) return 'Library Staff';
		if (this.userLevel === 3) return 'Library Admin';
		return 'Communico Staff';
	}

	render() {
		// `inert` only when the overlay is closed at narrow widths —
		// keeps the desktop sidebar tabbable. Setting `aria-hidden`
		// alongside focusable descendants flagged axe; `inert` is the
		// idiomatic answer (HTML5 attribute, well-supported now).
		const navInert = this._isNarrow && !this._navOpen;
		return html`
			<nav id="primary-nav" aria-label="Primary"
				class=${this._navOpen ? 'open' : ''}
				?inert=${navInert}
				@click=${this._onNavLinkClick}>
				<div class="brand">
					<img class="brand-logo" src="/images/brand/communico-one-logo-light.svg" alt="Communico One">
					<img class="brand-mark" src="/images/brand/communico-one-mark.svg" alt="Communico One">
				</div>
				${this.userLevel < 3 ? html`
					<div class="ws-switch" role="tablist" aria-label="Workspace">
						<button role="tab"
							class=${this._workspace === 'communico' ? 'on' : ''}
							aria-selected=${this._workspace === 'communico' ? 'true' : 'false'}
							data-label="Communico workspace"
							@click=${() => this._setWorkspace('communico')}>
							<cos-icon class="icon" name="dashboard" size="16"></cos-icon>
							<span class="nav-label">Communico</span>
						</button>
						<button role="tab"
							class=${this._workspace === 'partner' ? 'on' : ''}
							aria-selected=${this._workspace === 'partner' ? 'true' : 'false'}
							data-label="Partner products"
							@click=${() => this._setWorkspace('partner')}>
							<cos-icon class="icon" name="book-open" size="16"></cos-icon>
							<span class="nav-label">Partner</span>
						</button>
					</div>
				` : ''}
				${this._workspace === 'partner' && this.userLevel < 3 && this._legacyClients.length > 0
					? this._renderClientPick()
					: ''}
				${this._workspace === 'partner' && this.userLevel < 3
					? this._renderPartnerNav()
					: this._navItems().map((n) => this._renderNavRow(n))}
				<div class="spacer"></div>
				<button class="collapse-toggle"
					@click=${this._toggleCollapsed}
					aria-label=${this.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					aria-pressed=${this.collapsed ? 'true' : 'false'}
					title=${this.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
					<cos-icon class="icon" name=${this.collapsed ? 'arrow-right' : 'arrow-left'} size="18"></cos-icon>
					<span class="nav-label">Collapse</span>
				</button>
				${this._isDev() ? html`<div class="foot">${__COS_STAGE_LABEL__}</div>` : ''}
			</nav>
			<div class=${`backdrop ${this._navOpen ? 'open' : ''}`}
				@click=${this._closeNav}
				aria-hidden="true"></div>
			<header>
				<!-- The one breadcrumb trail (docs/design-system/
				     breadcrumb-trail.md §3): first child of the header, taking
				     the free left half, with the utilities right-aligned as
				     they always were. At narrow widths the CSS order property
				     puts the hamburger ahead of it (§7) without moving it in the
				     DOM, where reading order should still start with the trail. -->
				${this._renderTrail()}
				<button class="hamburger"
					@click=${this._toggleNav}
					aria-label=${this._navOpen ? 'Close menu' : 'Open menu'}
					aria-expanded=${this._navOpen ? 'true' : 'false'}
					aria-controls="primary-nav">
					<span class="bars" aria-hidden="true"></span>
				</button>
				<!-- Stage 38a — notification bell. Sits to the left
				     of the user meta block. The header's
				     justify-content: flex-end keeps both clustered
				     on the right (the hamburger floats left via
				     margin-right:auto in its own style block). -->
				${this.userLevel === 1 ? html`
					<div class="status-wrap">
						<button class="status-pill" @click=${() => { this._statusOpen = !this._statusOpen; }}
							aria-haspopup="menu" aria-expanded=${this._statusOpen ? 'true' : 'false'}
							aria-label=${`Status: ${this._myStatus || 'Active'}. Set your status`}
							title="Set your status">
							<span class="status-dot ${this._myStatus ? 'away' : 'on'}"></span>
							<span class="status-label">${this._myStatus || 'Active'}</span>
						</button>
						${this._statusOpen ? html`
							<div class="status-menu" role="menu">
								${['Active', 'Away', 'At lunch', 'In a meeting', 'Do not disturb'].map(opt => html`
									<button role="menuitem"
										class=${(this._myStatus || 'Active') === opt ? 'current' : ''}
										@click=${() => void this._setStatus(opt)}>
										<span class="status-dot ${opt === 'Active' ? 'on' : 'away'}"></span>${opt}
									</button>`)}
								<button role="menuitem" @click=${() => this._customStatus()}>Custom…</button>
							</div>` : ''}
					</div>` : ''}
				${this.userLevel === 1 ? html`
					<cos-feedback-widget .userLevel=${this.userLevel} .userPartnerId=${this.userPartnerId} .collapsed=${this.collapsed}></cos-feedback-widget>
				` : ''}
				<cos-notification-bell></cos-notification-bell>
				<div class="user">
					<div class="user-wrap">
						<button class="user-trigger"
							@click=${() => { this._userMenuOpen = !this._userMenuOpen; }}
							aria-haspopup="menu" aria-expanded=${this._userMenuOpen ? 'true' : 'false'}
							aria-label="Account menu">
							<span class="avatar" aria-hidden="true">${this._initials()}</span>
							<span class="user-meta">
								${this.userName || this.userEmail
									? html`<span class="user-name">${this.userName || this.userEmail}</span>`
									: html`<cos-skeleton variant="text" width="100px" height="14px"></cos-skeleton>`}
								<span class="user-role">${this._levelLabel()}</span>
							</span>
							<cos-icon name="chevron-down" size="14"></cos-icon>
						</button>
						${this._userMenuOpen ? html`
							<div class="user-menu" role="menu">
								<div class="menu-who">
									<span class="user-name">${this.userName || this.userEmail}</span>
									<span class="user-role">${this._levelLabel()}</span>
								</div>
								${this._impersonation
									? html`
											<!-- Impersonation (docs/impersonation-plan.md step 5): while a
											     session is live the menu's items collapse to just this —
											     Settings/Sign out for the REAL account don't apply to what
											     you're currently viewing, and the persistent banner already
											     carries the target/mode/time-remaining. -->
											<button role="menuitem" ?disabled=${this._stoppingImpersonation}
												@click=${() => void this._stopImpersonating()}>
												<cos-icon name="user-x" size="16"></cos-icon>Stop impersonating</button>
										`
									: html`
											<a role="menuitem" href="/settings"
												@click=${() => { this._userMenuOpen = false; }}>
												<cos-icon name="settings" size="16"></cos-icon>Settings</a>
											${this.userLevel === 1 ? html`
												<a role="menuitem" href="/activity" class="menu-note"
													@click=${() => { this._userMenuOpen = false; }}
													title="CommunicoOS records activity metadata (what kind of action, when; never content). Click to see your own numbers vs your role average.">
													Activity is logged</a>` : ''}
											${this.userLevel === 1 && this._canImpersonate && this._workspace === 'partner' ? html`
												<span class="divider"></span>
												<button role="menuitem" ?disabled=${this._awaitingClient()}
													title=${this._awaitingClient() ? 'Choose a library first' : ''}
													@click=${() => this._openImpersonateDialog()}>
													<cos-icon name="user-cog" size="16"></cos-icon>Impersonate a user…</button>` : ''}
											<span class="divider"></span>
											<button role="menuitem" @click=${this._logout}>
												<cos-icon name="log-out" size="16"></cos-icon>Sign out</button>
										`}
							</div>` : ''}
					</div>
				</div>
			</header>
			<main>
				${this._awaitingClient()
					? html`<div class="pick-first">
							<cos-empty-state
								tone="quiet"
								title="Choose a library"
								description=${this._legacyClientsFailed
									? 'Your libraries could not be loaded, so there is nothing to choose from yet.'
									: 'Communico staff have no library of their own, so nothing here belongs to anyone until you choose one.'}
							>
								${this._legacyClients.length > 0
									? html`<cos-button slot="action" variant="primary" @click=${() => this._focusClientPicker()}
											>Choose a library</cos-button
										>`
									: this._legacyClientsFailed
										? html`<cos-button slot="action" variant="primary" @click=${() => void this._loadLegacyClients(true)}
												>Try again</cos-button
											>`
										: nothing}
							</cos-empty-state>
						</div>`
					: this.path.startsWith('/admin')
						? html`
							<div class="admin-layout">
								<cos-admin-nav .path=${this.path} .cardholderPlural=${this.cardholderPlural}></cos-admin-nav>
								<div class="admin-body"><slot></slot></div>
							</div>
						`
						: html`<slot></slot>`}
			</main>
			${this._viewingAs
				? html`<div
						class="viewing-as"
						role="status"
						@click=${() => (this._viewingAs = null)}
					>
						<div class="viewing-as-card">
							You are viewing
							<strong>${this._viewingAs}</strong>
							<span class="hint">Click anywhere to continue</span>
						</div>
					</div>`
				: nothing}
			<!-- Impersonation (docs/impersonation-plan.md) — mounted
			     unconditionally (the cos-notification-bell precedent): the
			     component tracks the session itself, so it renders
			     regardless of which page/action started it and survives a
			     route change without the shell having to re-mount it. -->
			<cos-impersonation-banner></cos-impersonation-banner>
			${this._offline
				? html`<div class="conn-banner" role="status" aria-live="polite">
						No connection: changes can't save right now.
					</div>`
				: nothing}
			<cos-toast></cos-toast>
			${this._paletteMounted ? html`
				<cos-command-palette
					?open=${this._paletteOpen}
					.isAdmin=${this._isAdmin()}
					@palette-close=${this._onPaletteClose}></cos-command-palette>
			` : ''}
			${this._overlayMounted && this._overlayOpen ? html`
				<cos-shortcut-overlay
					@overlay-close=${() => { this._overlayOpen = false; }}></cos-shortcut-overlay>
			` : ''}
			${this._panelMounted ? html`
				<cos-side-panel
					?open=${this._panelState.open}
					title=${this._panelState.opts?.title ?? ''}
					width=${this._panelState.opts?.width ?? 'md'}
					?scrim=${this._panelState.opts?.scrim !== false}
					.tabs=${this._panelState.opts?.tabs}
					.activeTab=${this._panelState.activeTab}
					@panel-close=${() => sidePanelManager.close()}
					@tab-change=${(e: CustomEvent) => sidePanelManager.setActiveTab(e.detail.tabId)}>
					${this._panelState.opts
						? html`<div slot="body">${this._panelState.opts.renderBody(this._panelState.activeTab)}</div>`
						: ''}
					${this._panelState.opts?.renderFooter
						? html`<div slot="footer">${this._panelState.opts.renderFooter()}</div>`
						: ''}
				</cos-side-panel>
			` : ''}
			${this._createPartnerOpen ? html`
				<cos-create-partner-modal
					@close=${() => { this._createPartnerOpen = false; }}
					@created=${() => { this._createPartnerOpen = false; }}></cos-create-partner-modal>
			` : ''}
			<cos-impersonate-dialog
				?open=${this._impersonateDialogOpen}
				@close=${() => { this._impersonateDialogOpen = false; }}
				@started=${() => { this._impersonateDialogOpen = false; }}
			></cos-impersonate-dialog>
			${this._quickTaskOpen ? html`
				<cos-task-modal
					@close=${() => { this._quickTaskOpen = false; }}
					@saved=${() => { this._quickTaskOpen = false; }}></cos-task-modal>
			` : ''}
			${this._helpMounted ? this._renderHelp() : nothing}
			${this._isAdmin() ? html`
				<button class="agent-fab"
					style=${this._agentFabStyle()}
					@click=${this._toggleAgent}
					aria-label="Open agent (Cmd+/)"
					aria-expanded=${this._agentOpen ? 'true' : 'false'}
					title="Agent · Cmd+/">
					<cos-icon name="message-circle" size="22"></cos-icon>
				</button>
				${this._agentMounted ? html`
					<cos-agent-panel
						?open=${this._agentOpen}
						@agent-close=${this._onAgentClose}></cos-agent-panel>
				` : ''}
			` : ''}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-app-shell': CosAppShell;
	}
}
